import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Isotope from "isotope-layout";
import Swiper, { Navigation } from "swiper";

"use strict";

if (typeof gsap !== "undefined") {
	gsap.registerPlugin(MotionPathPlugin, ScrollTrigger);
}

(function injectSprite(window, document) {
	if (!window || !document || !window.file) return;
	if (
		!document.createElementNS ||
		!document.createElementNS("http://www.w3.org/2000/svg", "svg").createSVGRect
	) {
		return;
	}

	var isLocalStorage = "localStorage" in window && window.localStorage !== null;
	var request;
	var data;

	function insert() {
		if (document.body) {
			document.body.insertAdjacentHTML("afterbegin", data);
		} else {
			document.addEventListener("DOMContentLoaded", function () {
				document.body.insertAdjacentHTML("afterbegin", data);
			});
		}
	}

	if (isLocalStorage && localStorage.getItem("inlineSVGrev") === window.revision) {
		data = localStorage.getItem("inlineSVGdata");
		if (data) {
			insert();
			return;
		}
	}

	try {
		request = new XMLHttpRequest();
		request.open("GET", window.file, true);
		request.onload = function () {
			if (request.status >= 200 && request.status < 400) {
				data = request.responseText;
				insert();
				if (isLocalStorage) {
					localStorage.setItem("inlineSVGdata", data);
					localStorage.setItem("inlineSVGrev", window.revision);
				}
			}
		};
		request.send();
	} catch (e) {
		// noop
	}
})(window, document);

var scrollTopPos = 0;
var justClosedModal = false;
var modalIframe = null;
var bodyLockCount = 0;
var bodyLockScrollY = 0;
var lenisResizeRaf = null;
var suppressHeaderScrollUntil = 0;

function requestLenisResize() {
	if (!window.lenis || typeof window.lenis.resize !== "function") return;

	if (lenisResizeRaf) cancelAnimationFrame(lenisResizeRaf);
	lenisResizeRaf = requestAnimationFrame(function () {
		if (window.lenis && typeof window.lenis.resize === "function") {
			window.lenis.resize();
		}
		lenisResizeRaf = null;
	});
}

function suppressResizeObserverLoopError() {
	if (typeof window === "undefined") return;

	var warningText = "ResizeObserver loop completed with undelivered notifications.";

	window.addEventListener("error", function (event) {
		if (!event || typeof event.message !== "string") return;
		if (event.message.indexOf(warningText) === -1) return;
		event.stopImmediatePropagation();
	});
}

function syncMenuButtons() {
	var isMenuOpen = !!document.querySelector('[data-modal="menu"].is-show');
	var isSearchOpen = !!document.querySelector('[data-modal="search"].is-show');

	document.querySelectorAll(".header__icon-btn--menu[data-menu-trigger]").forEach(function (button) {
		button.classList.toggle("is-active", isMenuOpen);
	});

	document.querySelectorAll('.header__icon-btn--search[data-modal-toggle="search"]').forEach(function (button) {
		button.classList.toggle("is-active", isSearchOpen);
	});
}

function toggleBodyLock(action) {
	if (action === "lock") {
		bodyLockCount += 1;
		if (bodyLockCount > 1) return;

		bodyLockScrollY = window.scrollY || window.pageYOffset || 0;
		document.body.classList.add("lock", "full-lock");
		document.body.style.top = -bodyLockScrollY + "px";

		if (window.lenis && typeof window.lenis.stop === "function") {
			window.lenis.stop();
		}
		return;
	}

	bodyLockCount = Math.max(0, bodyLockCount - 1);
	if (bodyLockCount !== 0) return;

	document.body.classList.remove("lock", "full-lock");
	document.body.style.top = "";
	window.scrollTo(0, bodyLockScrollY);
	bodyLockScrollY = 0;

	if (window.lenis && typeof window.lenis.start === "function") {
		window.lenis.start();
	}
}

function createVideoIframe(videoUrl, container) {
	if (!container || !videoUrl) return null;

	var embedUrl = "";
	if (videoUrl.indexOf("youtube.com") > -1 || videoUrl.indexOf("youtu.be") > -1) {
		var parts = videoUrl.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
		var videoId = parts[2] ? parts[2].split(/[^0-9a-z_-]/i)[0] : "";
		embedUrl = "https://www.youtube.com/embed/" + videoId + "?autoplay=1&enablejsapi=1";
	} else if (videoUrl.indexOf("vimeo.com") > -1) {
		var vimeoId = videoUrl.split("/").pop();
		embedUrl = "https://player.vimeo.com/video/" + vimeoId + "?autoplay=1&api=1&background=0";
	} else {
		return null;
	}

	var iframe = document.createElement("iframe");
	iframe.src = embedUrl;
	iframe.setAttribute("allow", "autoplay; encrypted-media; fullscreen; picture-in-picture");
	iframe.setAttribute("frameborder", "0");
	iframe.setAttribute("allowfullscreen", "true");
	container.innerHTML = "";
	container.appendChild(iframe);
	return iframe;
}

function pauseVideo(iframe) {
	if (!iframe) return;
	var frameWindow = iframe.contentWindow;
	if (frameWindow) {
		if (iframe.src.indexOf("youtube.com") > -1) {
			frameWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
		}
		if (iframe.src.indexOf("vimeo.com") > -1) {
			frameWindow.postMessage('{"method":"pause"}', "*");
		}
	}
	iframe.remove();
	if (iframe === modalIframe) modalIframe = null;
}

function focusSearchInput(modal) {
	if (!modal) return;
	var searchInput = modal.querySelector(".s-search__input input, .s-search__desktop-card input");
	if (!searchInput) return;

	try {
		searchInput.focus({ preventScroll: true });
	} catch (error) {
		searchInput.focus();
	}

	if (typeof searchInput.setSelectionRange === "function") {
		var valueLength = searchInput.value ? searchInput.value.length : 0;
		searchInput.setSelectionRange(valueLength, valueLength);
	}
}

function openModal(modalName, url) {
	var modal = document.querySelector('[data-modal="' + modalName + '"]');
	if (!modal) return;
	var isMobileViewport =
		typeof window !== "undefined" &&
		window.matchMedia &&
		window.matchMedia("(max-width: 1199px)").matches;

	Array.from(document.querySelectorAll("[data-modal].is-show")).forEach(function (opened) {
		var openedName = opened.getAttribute("data-modal");
		if (openedName !== modalName) {
			closeModal(openedName, { skipScrollSync: true });
		}
	});

	modal.classList.remove("is-hide");
	modal.classList.add("is-show");
	document.body.classList.add("modal-open");

	if (modal.dataset.modalType === "iframe") {
		var container = modal.querySelector(".s-modal__iframe");
		modalIframe = createVideoIframe(url, container);
	}

	if (modalName === "menu") {
		document.body.classList.add("is-menu-open");
		syncMenuButtons();
	}

	if (modalName === "search") {
		if (isMobileViewport) {
			document.body.classList.add("is-menu-open");
			syncMenuButtons();
		}

		// Keep a synchronous attempt for mobile browsers that block delayed autofocus.
		focusSearchInput(modal);

		requestAnimationFrame(function () {
			focusSearchInput(modal);
		});

		if (isMobileViewport) {
			var searchCard = modal.querySelector(".s-search__desktop-card");
			if (searchCard) {
				var hasFocusedAfterTransition = false;
				var focusAfterTransition = function () {
					if (hasFocusedAfterTransition) return;
					hasFocusedAfterTransition = true;
					searchCard.removeEventListener("transitionend", onCardTransitionEnd);
					if (!modal.classList.contains("is-show")) return;
					focusSearchInput(modal);
				};
				var onCardTransitionEnd = function (event) {
					if (event.target !== searchCard) return;
					if (event.propertyName !== "transform" && event.propertyName !== "opacity") return;
					focusAfterTransition();
				};

				searchCard.addEventListener("transitionend", onCardTransitionEnd);
				setTimeout(focusAfterTransition, 420);
			}
		}
	}

	if (modalName === "event-book") {
		var triggerButton = document.querySelector('[data-modal-toggle="event-book"]');
		if (triggerButton && triggerButton.dataset.attendeeCount) {
			var availabilityValue = modal.querySelector('[data-event-availability-value]');
			if (availabilityValue) {
				availabilityValue.textContent = triggerButton.dataset.attendeeCount;
			}
		}
	}

	toggleBodyLock("lock");
}

function closeModal(modalName, options) {
	var modal = document.querySelector('[data-modal="' + modalName + '"]');
	if (!modal) return;
	var opts = options || {};
	var skipScrollSync = !!opts.skipScrollSync;
	var isMobileViewport =
		typeof window !== "undefined" &&
		window.matchMedia &&
		window.matchMedia("(max-width: 1199px)").matches;

	if (skipScrollSync) {
		suppressHeaderScrollUntil = Date.now() + 500;
	}

	modal.classList.remove("is-show");
	modal.classList.add("is-hide");

	if (modal.dataset.modalType === "iframe") {
		pauseVideo(modalIframe);
	}

	if (modalName === "menu") {
		document.body.classList.remove("is-menu-open");
		syncMenuButtons();
	}

	if (modalName === "search" && isMobileViewport && !document.querySelector('[data-modal="menu"].is-show')) {
		document.body.classList.remove("is-menu-open");
		syncMenuButtons();
	}

	if (!document.querySelector("[data-modal].is-show")) {
		document.body.classList.remove("modal-open");
		toggleBodyLock("unlock");
	}

	if (modalName === "search" && isMobileViewport) {
		if (skipScrollSync) {
			justClosedModal = false;
			scrollTopPos = window.scrollY || window.pageYOffset || 0;
			return;
		}

		suppressHeaderScrollUntil = Date.now() + 500;
		scrollTopPos = window.scrollY || window.pageYOffset || 0;
		justClosedModal = false;
		return;
	}

	if (skipScrollSync) {
		justClosedModal = false;
		scrollTopPos = window.scrollY || window.pageYOffset || 0;
		return;
	}

	justClosedModal = true;
	window.dispatchEvent(new Event("scroll"));
}

function toggleModal(modalName, url) {
	var modal = document.querySelector('[data-modal="' + modalName + '"]');
	if (!modal) return;
	if (modal.classList.contains("is-show")) {
		closeModal(modalName);
	} else {
		openModal(modalName, url);
	}
}

function initModals() {
	document.querySelectorAll("[data-modal-toggle]").forEach(function (toggle) {
		toggle.addEventListener("click", function (event) {
			if (toggle.tagName === "A") {
				event.preventDefault();
			}
			var modalName = toggle.getAttribute("data-modal-toggle");
			var url = toggle.dataset.url !== undefined ? toggle.dataset.url : null;
			toggleModal(modalName, url);
		});
	});

	document.addEventListener("click", function (event) {
		var openModalEl = document.querySelector("[data-modal].is-show");
		if (!openModalEl) return;

		if (openModalEl.classList.contains("s-menu") && event.target === openModalEl) {
			closeModal(openModalEl.getAttribute("data-modal"));
			return;
		}

		if (event.target.classList.contains("s-modal__wrapper")) {
			closeModal(openModalEl.getAttribute("data-modal"));
		}
	});

	document.addEventListener("keydown", function (event) {
		if (event.key !== "Escape") return;
		Array.from(document.querySelectorAll("[data-modal].is-show")).forEach(function (modal) {
			closeModal(modal.getAttribute("data-modal"));
		});
	});
}

function initAccordions() {
	var roots = Array.from(document.querySelectorAll("[data-accordion-root]"));
	if (!roots.length) return;

	roots.forEach(function (root) {
		var items = Array.from(root.querySelectorAll("[data-accordion-item]"));
		if (!items.length) return;

		var isSingle = root.hasAttribute("data-accordion-single");

		function setState(item, isOpen, skipAnimation) {
			var trigger = item.querySelector("[data-accordion-trigger]");
			var panel = item.querySelector("[data-accordion-panel]");
			if (!trigger || !panel) return;
			var hasDelayedOverflow = panel.hasAttribute("data-accordion-overflow-delayed");

			if (hasDelayedOverflow) {
				panel.classList.remove("is-overflow-visible");
			}

			item.classList.toggle("is-open", isOpen);
			trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");

			if (skipAnimation) {
				panel.style.maxHeight = isOpen ? "none" : "0px";
				if (isOpen) {
					requestAnimationFrame(function () {
						panel.style.maxHeight = panel.scrollHeight + "px";
						if (hasDelayedOverflow) {
							panel.classList.add("is-overflow-visible");
						}
						requestLenisResize();
					});
				} else {
					requestLenisResize();
				}
				return;
			}

			panel.style.maxHeight = isOpen ? panel.scrollHeight + "px" : "0px";
			requestLenisResize();
		}

		items.forEach(function (item) {
			setState(item, item.classList.contains("is-open"), true);
			var trigger = item.querySelector("[data-accordion-trigger]");
			if (!trigger) return;
			var panel = item.querySelector("[data-accordion-panel]");
				if (panel) {
					panel.addEventListener("transitionend", function (event) {
						if (event.propertyName !== "max-height") return;
						if (panel.hasAttribute("data-accordion-overflow-delayed")) {
							panel.classList.toggle("is-overflow-visible", item.classList.contains("is-open"));
						}
						requestLenisResize();
					});
				}

			trigger.addEventListener("click", function () {
				var willOpen = !item.classList.contains("is-open");
				if (isSingle && willOpen) {
					items.forEach(function (otherItem) {
						if (otherItem === item) return;
						setState(otherItem, false, false);
					});
				}
				setState(item, willOpen, false);
			});
		});

		window.addEventListener("resize", function () {
			items.forEach(function (item) {
				if (!item.classList.contains("is-open")) return;
				var panel = item.querySelector("[data-accordion-panel]");
				if (!panel) return;
				panel.style.maxHeight = panel.scrollHeight + "px";
			});
			requestLenisResize();
		});
	});
}

function initEventsFilters() {
	var root = document.querySelector("[data-events-page]");
	if (!root) return;

	var list = root.querySelector("[data-events-list]");
	var cards = Array.from(root.querySelectorAll("[data-event-item]"));
	if (!list || !cards.length) return;

	var categoryButtons = Array.from(root.querySelectorAll("[data-events-category-btn]"));
	var audienceButtons = Array.from(root.querySelectorAll("[data-events-audience-btn]"));
	var audienceSelect = root.querySelector("[data-events-audience-select]");
	var audienceDropdown = root.querySelector("[data-events-audience-dropdown]");
	var audienceToggle = root.querySelector("[data-events-audience-toggle]");
	var audienceSelected = root.querySelector("[data-events-audience-selected]");
	var mobileAudienceLabel = root.querySelector("[data-events-mobile-audience-label]");
	var emptyState = root.querySelector("[data-events-empty]");
	var currentCategory = "all";
	var currentAudience = audienceSelect ? (audienceSelect.value || "") : "";

	function normalizeValue(value) {
		return (value || "").toLowerCase().trim();
	}

	function parseCsv(value) {
		if (!value) return [];
		return value.split(",").map(function (item) {
			return normalizeValue(item);
		}).filter(Boolean);
	}

	function getGapPx() {
		var raw = getComputedStyle(list).getPropertyValue("--events-list-gap");
		var value = (raw || "").trim();
		if (!value) return 32;

		var parsed = parseFloat(value);
		if (!Number.isFinite(parsed)) return 32;

		if (value.endsWith("px")) return parsed;
		if (value.endsWith("rem")) {
			var rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
			return Number.isFinite(rootFontSize) ? parsed * rootFontSize : parsed * 16;
		}
		if (value.endsWith("em")) {
			var listFontSize = parseFloat(getComputedStyle(list).fontSize);
			return Number.isFinite(listFontSize) ? parsed * listFontSize : parsed * 16;
		}

		return parsed;
	}

	function getAudienceLabel() {
		if (!currentAudience) return "Please select";

		if (audienceSelect) {
			var matchedOption = Array.from(audienceSelect.options).find(function (option) {
				return normalizeValue(option.value) === currentAudience;
			});
			if (matchedOption) return matchedOption.textContent || "Please select";
		}

		var matchedButton = audienceButtons.find(function (button) {
			return normalizeValue(button.getAttribute("data-audience-value")) === currentAudience;
		});
		if (matchedButton) return matchedButton.textContent || "Please select";

		return "Please select";
	}

	function cardMatches(card) {
		var cardCategory = normalizeValue(card.getAttribute("data-event-category"));
		var cardAudienceValues = parseCsv(card.getAttribute("data-event-audience"));
		var categoryMatches = currentCategory === "all" || cardCategory === currentCategory;
		var audienceMatches = !currentAudience || currentAudience === "all-staff" || cardAudienceValues.indexOf(currentAudience) > -1;
		return categoryMatches && audienceMatches;
	}

	function setAudienceDropdownState(isOpen) {
		if (!audienceDropdown) return;
		audienceDropdown.classList.toggle("is-open", isOpen);
		if (audienceToggle) {
			audienceToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
		}
	}

	function syncControls() {
		categoryButtons.forEach(function (button) {
			var buttonCategory = normalizeValue(button.getAttribute("data-category-value"));
			var isActive = buttonCategory === currentCategory;
			button.classList.toggle("is-active", isActive);
			button.setAttribute("aria-pressed", isActive ? "true" : "false");
		});

		audienceButtons.forEach(function (button) {
			var buttonAudience = normalizeValue(button.getAttribute("data-audience-value"));
			var isActive = buttonAudience === currentAudience;
			button.classList.toggle("is-active", isActive);
			button.setAttribute("aria-pressed", isActive ? "true" : "false");
			if (button.getAttribute("role") === "option") {
				button.setAttribute("aria-selected", isActive ? "true" : "false");
			}
		});

		if (audienceSelect && audienceSelect.value !== currentAudience) {
			audienceSelect.value = currentAudience;
		}

		if (audienceSelected) {
			audienceSelected.textContent = getAudienceLabel();
		}

		if (mobileAudienceLabel) {
			mobileAudienceLabel.textContent = getAudienceLabel();
		}
	}

	function relayout() {
		var gap = getGapPx();
		var currentY = 0;
		var visibleCount = 0;

		cards.forEach(function (card) {
			var isVisible = cardMatches(card);
			card.classList.toggle("is-filtered-out", !isVisible);
			card.setAttribute("aria-hidden", isVisible ? "false" : "true");
			card.style.transform = "translate3d(0," + Math.round(currentY) + "px,0)";
			card.style.opacity = isVisible ? "1" : "0";

			if (!isVisible) return;
			visibleCount += 1;
			currentY += card.offsetHeight + gap;
		});

		if (visibleCount > 0) {
			currentY -= gap;
		} else {
			currentY = 0;
		}

		list.style.height = Math.max(0, Math.round(currentY)) + "px";
		if (emptyState) {
			emptyState.hidden = visibleCount !== 0;
		}
		requestLenisResize();
	}

	function applyFilters() {
		syncControls();
		relayout();
	}

	list.classList.add("is-enhanced");
	applyFilters();
	requestAnimationFrame(function () {
		list.classList.add("is-animated");
		relayout();
	});

	categoryButtons.forEach(function (button) {
		button.addEventListener("click", function () {
			var nextCategory = normalizeValue(button.getAttribute("data-category-value")) || "all";
			if (nextCategory === currentCategory) return;
			currentCategory = nextCategory;
			applyFilters();
		});
	});

	audienceButtons.forEach(function (button) {
		button.addEventListener("click", function () {
			var nextAudience = normalizeValue(button.getAttribute("data-audience-value"));
			if (nextAudience === currentAudience) {
				if (audienceDropdown && audienceDropdown.contains(button)) {
					setAudienceDropdownState(false);
				}
				return;
			}
			currentAudience = nextAudience;
			applyFilters();
			if (audienceDropdown && audienceDropdown.contains(button)) {
				setAudienceDropdownState(false);
			}
		});
	});

	if (audienceSelect) {
		audienceSelect.addEventListener("change", function () {
			currentAudience = normalizeValue(audienceSelect.value);
			applyFilters();
		});
	}

	if (audienceToggle) {
		audienceToggle.addEventListener("click", function () {
			var isOpen = audienceDropdown ? audienceDropdown.classList.contains("is-open") : false;
			setAudienceDropdownState(!isOpen);
		});
	}

	document.addEventListener("click", function (event) {
		if (!audienceDropdown || !audienceDropdown.classList.contains("is-open")) return;
		if (audienceDropdown.contains(event.target)) return;
		setAudienceDropdownState(false);
	});

	document.addEventListener("keydown", function (event) {
		if (event.key !== "Escape") return;
		setAudienceDropdownState(false);
	});

	var resizeRaf = null;
	window.addEventListener("resize", function () {
		if (resizeRaf) cancelAnimationFrame(resizeRaf);
		resizeRaf = requestAnimationFrame(function () {
			relayout();
			setAudienceDropdownState(false);
			resizeRaf = null;
		});
	});

	// Clear filters link.
	var clearLink = root.querySelector("[data-events-clear-filters]");
	if (clearLink) {
		clearLink.addEventListener("click", function (event) {
			event.preventDefault();
			currentCategory = "all";
			currentAudience = "";
			applyFilters();
			setAudienceDropdownState(false);
		});
	}

	window.addEventListener("load", relayout);
	if (document.fonts && typeof document.fonts.ready === "object" && typeof document.fonts.ready.then === "function") {
		document.fonts.ready.then(relayout);
	}
}

function initNewsResourcesFilters() {
	var filtersForm = document.querySelector("[data-news-filters]");
	var list = document.querySelector("[data-news-list]");
	if (!filtersForm || !list) return;

	var configEl = document.getElementById("news-archive-config");
	var subcatMapEl = document.getElementById("news-subcategory-map");
	if (!configEl) return;

	var config = {};
	var subcatMap = {};
	try { config = JSON.parse(configEl.textContent || "{}"); } catch (e) { return; }
	try { subcatMap = JSON.parse((subcatMapEl && subcatMapEl.textContent) || "{}"); } catch (e) { /* noop */ }

	var categorySelect = document.querySelector("[data-news-category-select]");
	var subcategorySelect = document.querySelector("[data-news-subcategory-select]");
	var audienceSelect = document.querySelector("[data-news-audience-select]");
	var keywordInput = document.querySelector("[data-news-keyword-input]");
	var searchButton = document.querySelector("[data-news-search-button]");
	var categoryDropdown = document.querySelector("[data-news-category-dropdown]");
	var categoryToggle = document.querySelector("[data-news-category-toggle]");
	var categorySelected = document.querySelector("[data-news-category-selected]");
	var categoryButtons = Array.from(document.querySelectorAll("[data-news-category-option]"));
	var subcategoryWrap = document.querySelector("[data-news-subcategory-wrap]");
	var subcategoryDropdown = document.querySelector("[data-news-subcategory-dropdown]");
	var subcategoryToggle = document.querySelector("[data-news-subcategory-toggle]");
	var subcategorySelected = document.querySelector("[data-news-subcategory-selected]");
	var subcategoryMenu = document.querySelector("[data-news-subcategory-menu]");
	var audienceDropdown = document.querySelector("[data-news-audience-dropdown]");
	var audienceToggle = document.querySelector("[data-news-audience-toggle]");
	var audienceSelected = document.querySelector("[data-news-audience-selected]");
	var audienceButtons = Array.from(document.querySelectorAll("[data-news-audience-option]"));
	var emptyState = document.querySelector("[data-news-empty]");
	var pagination = document.querySelector("[data-news-pagination]");
	if (!categorySelect || !audienceSelect || !keywordInput) return;

	filtersForm.addEventListener("submit", function (event) {
		event.preventDefault();
	});

	var currentPage = config.currentPage || 1;
	var totalPages = config.totalPages || 1;
	var baseUrl = config.baseUrl || window.location.pathname.replace(/\/page\/\d+\/?$/, "");
	var isLoading = false;
	var debounceTimer = null;
	var hasActiveFilters = false;

	function getPageUrl(page) {
		if (page <= 1) return baseUrl + "/";
		return baseUrl + "/page/" + page + "/";
	}

	function pushPageUrl(page) {
		var url = getPageUrl(page);
		if (window.location.pathname !== url) {
			history.pushState({ newsPage: page }, "", url);
		}
	}

	function normalizeValue(value) {
		return (value || "").toLowerCase().trim();
	}

	function setDropdownState(dropdown, toggle, isOpen) {
		if (!dropdown || !toggle) return;
		dropdown.classList.toggle("is-open", isOpen);
		toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
	}

	function closeAllDropdowns(exceptDropdown) {
		[
			{ dropdown: categoryDropdown, toggle: categoryToggle },
			{ dropdown: subcategoryDropdown, toggle: subcategoryToggle },
			{ dropdown: audienceDropdown, toggle: audienceToggle },
		].forEach(function (item) {
			if (!item.dropdown || !item.toggle) return;
			if (exceptDropdown && item.dropdown === exceptDropdown) return;
			setDropdownState(item.dropdown, item.toggle, false);
		});
	}

	function getOptionLabel(select, value, fallback) {
		var normalized = normalizeValue(value);
		if (!normalized) return fallback;
		var option = Array.from(select.options).find(function (item) {
			return normalizeValue(item.value) === normalized;
		});
		return option ? option.textContent : fallback;
	}

	function syncOptionButtons(buttons, value) {
		var normalized = normalizeValue(value);
		buttons.forEach(function (button) {
			var buttonValue = normalizeValue(button.getAttribute("data-value"));
			var isActive = buttonValue === normalized;
			button.classList.toggle("is-active", isActive);
			button.setAttribute("aria-selected", isActive ? "true" : "false");
		});
	}

	function syncCategoryControls() {
		if (categorySelected) {
			categorySelected.textContent = getOptionLabel(categorySelect, categorySelect.value, "All");
		}
		syncOptionButtons(categoryButtons, categorySelect.value);
	}

	function syncAudienceControls() {
		if (audienceSelected) {
			audienceSelected.textContent = getOptionLabel(audienceSelect, audienceSelect.value, "Please select");
		}
		syncOptionButtons(audienceButtons, audienceSelect.value);
	}

	function getSubcategoryButtons() {
		if (!subcategoryMenu) return [];
		return Array.from(subcategoryMenu.querySelectorAll("[data-news-subcategory-option]"));
	}

	function syncSubcategoryControls() {
		if (!subcategorySelect) return;
		if (subcategorySelected) {
			var fallback = subcategorySelect.options.length > 0 ? subcategorySelect.options[0].textContent : "Sub category";
			subcategorySelected.textContent = getOptionLabel(subcategorySelect, subcategorySelect.value, fallback);
		}
		syncOptionButtons(getSubcategoryButtons(), subcategorySelect.value);
	}

	function toggleSubcategoryState(isDisabled) {
		if (!subcategorySelect) return;
		subcategorySelect.disabled = isDisabled;
		if (subcategoryToggle) {
			subcategoryToggle.disabled = isDisabled;
		}
		if (subcategoryWrap) {
			subcategoryWrap.classList.toggle("is-disabled", isDisabled);
		}
		if (isDisabled) {
			setDropdownState(subcategoryDropdown, subcategoryToggle, false);
		}
	}

	function rebuildSubcategoryOptions(resetValue) {
		if (!subcategorySelect) return;
		var selectedCategory = normalizeValue(categorySelect.value);
		var children = selectedCategory && subcatMap[selectedCategory] ? subcatMap[selectedCategory] : [];

		subcategorySelect.innerHTML = "";
		if (subcategoryMenu) {
			subcategoryMenu.innerHTML = "";
		}

		var hasChildren = children.length > 0;
		var placeholderLabel = hasChildren ? "All" : "Sub category";

		var placeholder = document.createElement("option");
		placeholder.value = "";
		placeholder.textContent = placeholderLabel;
		subcategorySelect.appendChild(placeholder);
		if (subcategoryMenu) {
			var placeholderButton = document.createElement("button");
			placeholderButton.className = "s-filter__option is-active";
			placeholderButton.type = "button";
			placeholderButton.setAttribute("role", "option");
			placeholderButton.setAttribute("aria-selected", "true");
			placeholderButton.setAttribute("data-news-subcategory-option", "");
			placeholderButton.setAttribute("data-value", "");
			placeholderButton.textContent = placeholderLabel;
			subcategoryMenu.appendChild(placeholderButton);
		}

		children.forEach(function (child) {
			var option = document.createElement("option");
			option.value = child.slug;
			option.textContent = child.name;
			subcategorySelect.appendChild(option);

			if (subcategoryMenu) {
				var optionButton = document.createElement("button");
				optionButton.className = "s-filter__option";
				optionButton.type = "button";
				optionButton.setAttribute("role", "option");
				optionButton.setAttribute("aria-selected", "false");
				optionButton.setAttribute("data-news-subcategory-option", "");
				optionButton.setAttribute("data-value", child.slug);
				optionButton.textContent = child.name;
				subcategoryMenu.appendChild(optionButton);
			}
		});

		toggleSubcategoryState(children.length === 0);

		if (resetValue !== false) {
			subcategorySelect.value = "";
		}
		syncSubcategoryControls();
	}

	function getPageRange(current, total) {
		if (total <= 7) {
			var all = [];
			for (var i = 1; i <= total; i++) all.push(i);
			return all;
		}
		var pages = [1];
		var rangeStart = Math.max(2, current - 1);
		var rangeEnd = Math.min(total - 1, current + 1);
		if (current <= 3) { rangeStart = 2; rangeEnd = Math.min(4, total - 1); }
		if (current >= total - 2) { rangeStart = Math.max(2, total - 3); rangeEnd = total - 1; }
		if (rangeStart > 2) pages.push("...");
		for (var j = rangeStart; j <= rangeEnd; j++) pages.push(j);
		if (rangeEnd < total - 1) pages.push("...");
		pages.push(total);
		return pages;
	}

	function buildPagination(current, total) {
		if (!pagination) return;
		if (total <= 1) {
			pagination.innerHTML = "";
			pagination.hidden = true;
			return;
		}
		pagination.hidden = false;
		var ul = document.createElement("ul");
		ul.className = "page-numbers";

		if (current > 1) {
			var prevLi = document.createElement("li");
			var prevA = document.createElement("a");
			prevA.className = "prev page-numbers";
			prevA.href = getPageUrl(current - 1);
			prevA.setAttribute("aria-label", "Previous page");
			prevA.innerHTML = '<svg aria-hidden="true"><use xlink:href="#icon-link-arrow"></use></svg>';
			prevA.setAttribute("data-news-page", String(current - 1));
			prevLi.appendChild(prevA);
			ul.appendChild(prevLi);
		}

		var range = getPageRange(current, total);
		range.forEach(function (p) {
			var li = document.createElement("li");
			if (p === "...") {
				var dots = document.createElement("span");
				dots.className = "page-numbers dots";
				dots.textContent = "\u2026";
				li.appendChild(dots);
			} else if (p === current) {
				var span = document.createElement("span");
				span.className = "page-numbers current";
				span.setAttribute("aria-current", "page");
				span.textContent = String(p);
				li.appendChild(span);
			} else {
				var a = document.createElement("a");
				a.className = "page-numbers";
				a.href = getPageUrl(p);
				a.setAttribute("data-news-page", String(p));
				a.textContent = String(p);
				li.appendChild(a);
			}
			ul.appendChild(li);
		});

		if (current < total) {
			var nextLi = document.createElement("li");
			var nextA = document.createElement("a");
			nextA.className = "next page-numbers";
			nextA.href = getPageUrl(current + 1);
			nextA.setAttribute("aria-label", "Next page");
			nextA.innerHTML = '<svg aria-hidden="true"><use xlink:href="#icon-link-arrow"></use></svg>';
			nextA.setAttribute("data-news-page", String(current + 1));
			nextLi.appendChild(nextA);
			ul.appendChild(nextLi);
		}

		pagination.innerHTML = "";
		pagination.appendChild(ul);
	}

	function fetchPosts(page) {
		if (isLoading) return;
		isLoading = true;
		list.classList.add("is-loading");

		var formData = new FormData();
		formData.append("action", "lea_news_filter");
		formData.append("nonce", config.nonce);
		formData.append("category", categorySelect.value);
		formData.append("subcategory", subcategorySelect ? subcategorySelect.value : "");
		formData.append("audience", audienceSelect.value);
		formData.append("keyword", keywordInput.value);
		formData.append("page", page);
		formData.append("per_page", config.perPage);

		var xhr = new XMLHttpRequest();
		xhr.open("POST", config.ajaxUrl, true);
		xhr.onload = function () {
			isLoading = false;
			list.classList.remove("is-loading");

			if (xhr.status < 200 || xhr.status >= 400) return;

			var response;
			try { response = JSON.parse(xhr.responseText); } catch (e) { return; }
			if (!response || !response.success) return;

			var data = response.data;
			list.innerHTML = data.html;
			currentPage = data.currentPage;
			totalPages = data.totalPages;

			// Fade in new cards — they start at opacity:0 via in-view-detect-tag > *
			// Adding in-view after a frame triggers the CSS transition.
			requestAnimationFrame(function () {
				var newCards = list.querySelectorAll("[data-news-item]");
				newCards.forEach(function (card) {
					card.classList.add("in-view");
				});
			});

			if (emptyState) {
				emptyState.hidden = data.foundPosts > 0;
			}

			buildPagination(currentPage, totalPages);
			requestLenisResize();
		};
		xhr.onerror = function () {
			isLoading = false;
			list.classList.remove("is-loading");
		};
		xhr.send(formData);
	}

	function checkHasActiveFilters() {
		hasActiveFilters = !!(categorySelect.value || (subcategorySelect && subcategorySelect.value) || audienceSelect.value || keywordInput.value);
	}

	function fetchPage(page, updateUrl) {
		fetchPosts(page);
		if (updateUrl !== false && !hasActiveFilters) {
			pushPageUrl(page);
		}
	}

	function applyFilters() {
		currentPage = 1;
		checkHasActiveFilters();
		if (hasActiveFilters) {
			pushPageUrl(1);
		}
		fetchPosts(1);
	}

	function handleSelectOption(select, value) {
		if (select.value === value) return false;
		select.value = value;
		return true;
	}

	categoryButtons.forEach(function (button) {
		button.addEventListener("click", function () {
			var value = normalizeValue(button.getAttribute("data-value"));
			var changed = handleSelectOption(categorySelect, value);
			syncCategoryControls();
			if (changed) {
				rebuildSubcategoryOptions();
				applyFilters();
			}
			setDropdownState(categoryDropdown, categoryToggle, false);
		});
	});

	if (subcategoryMenu) {
		subcategoryMenu.addEventListener("click", function (event) {
			var button = event.target.closest("[data-news-subcategory-option]");
			if (!button) return;
			var value = normalizeValue(button.getAttribute("data-value"));
			var changed = handleSelectOption(subcategorySelect, value);
			syncSubcategoryControls();
			if (changed) {
				applyFilters();
			}
			setDropdownState(subcategoryDropdown, subcategoryToggle, false);
		});
	}

	audienceButtons.forEach(function (button) {
		button.addEventListener("click", function () {
			var value = normalizeValue(button.getAttribute("data-value"));
			var changed = handleSelectOption(audienceSelect, value);
			syncAudienceControls();
			if (changed) {
				applyFilters();
			}
			setDropdownState(audienceDropdown, audienceToggle, false);
		});
	});

	if (categoryToggle) {
		categoryToggle.addEventListener("click", function () {
			var isOpen = categoryDropdown && categoryDropdown.classList.contains("is-open");
			closeAllDropdowns(categoryDropdown);
			setDropdownState(categoryDropdown, categoryToggle, !isOpen);
		});
	}

	if (subcategoryToggle) {
		subcategoryToggle.addEventListener("click", function () {
			if (subcategoryToggle.disabled) return;
			var isOpen = subcategoryDropdown && subcategoryDropdown.classList.contains("is-open");
			closeAllDropdowns(subcategoryDropdown);
			setDropdownState(subcategoryDropdown, subcategoryToggle, !isOpen);
		});
	}

	if (audienceToggle) {
		audienceToggle.addEventListener("click", function () {
			var isOpen = audienceDropdown && audienceDropdown.classList.contains("is-open");
			closeAllDropdowns(audienceDropdown);
			setDropdownState(audienceDropdown, audienceToggle, !isOpen);
		});
	}

	document.addEventListener("click", function (event) {
		var target = event.target;
		if (
			(categoryDropdown && categoryDropdown.contains(target)) ||
			(subcategoryDropdown && subcategoryDropdown.contains(target)) ||
			(audienceDropdown && audienceDropdown.contains(target))
		) {
			return;
		}
		closeAllDropdowns();
	});

	document.addEventListener("keydown", function (event) {
		if (event.key !== "Escape") return;
		closeAllDropdowns();
	});

	function scrollToList() {
		var listSection = list.closest(".s-news-list");
		if (!listSection) return;
		var header = document.querySelector(".header");
		var headerHeight = header ? header.offsetHeight : 0;
		var offset = headerHeight + 32;
		var top = listSection.getBoundingClientRect().top + window.scrollY - offset;
		window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
	}

	if (pagination) {
		pagination.addEventListener("click", function (event) {
			var link = event.target.closest("[data-news-page]");
			if (!link) return;
			event.preventDefault();
			var page = parseInt(link.getAttribute("data-news-page"), 10);
			if (page && page !== currentPage) {
				fetchPage(page);
				scrollToList();
			}
		});
	}

	// Browser back/forward.
	window.addEventListener("popstate", function (event) {
		var page = 1;
		if (event.state && event.state.newsPage) {
			page = event.state.newsPage;
		} else {
			var match = window.location.pathname.match(/\/page\/(\d+)\/?$/);
			if (match) page = parseInt(match[1], 10);
		}
		if (page !== currentPage) {
			fetchPosts(page);
		}
	});

	categorySelect.addEventListener("change", function () {
		syncCategoryControls();
		rebuildSubcategoryOptions();
		applyFilters();
	});
	if (subcategorySelect) {
		subcategorySelect.addEventListener("change", function () {
			syncSubcategoryControls();
			applyFilters();
		});
	}
	audienceSelect.addEventListener("change", function () {
		syncAudienceControls();
		applyFilters();
	});

	keywordInput.addEventListener("input", function () {
		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(applyFilters, 300);
	});

	if (searchButton) {
		searchButton.addEventListener("click", function () {
			clearTimeout(debounceTimer);
			applyFilters();
			keywordInput.focus();
		});
	}

	// Clear filters link.
	var clearLink = document.querySelector("[data-news-clear-filters]");
	if (clearLink) {
		clearLink.addEventListener("click", function (event) {
			event.preventDefault();
			categorySelect.value = "";
			if (subcategorySelect) subcategorySelect.value = "";
			audienceSelect.value = "";
			keywordInput.value = "";
			syncCategoryControls();
			syncAudienceControls();
			rebuildSubcategoryOptions();
			applyFilters();
		});
	}

	// Replace current history entry so back works from page N.
	if (currentPage > 1) {
		history.replaceState({ newsPage: currentPage }, "", getPageUrl(currentPage));
	}

	syncCategoryControls();
	syncAudienceControls();
	rebuildSubcategoryOptions(false);
	buildPagination(currentPage, totalPages);
}

function initDirectorBioModal() {
	var modal = document.querySelector('[data-modal="bio"]');
	var dataScript = document.getElementById("director-bios-data");
	if (!modal || !dataScript) return;

	var data = [];
	try {
		data = JSON.parse(dataScript.textContent || "[]");
	} catch (error) {
		data = [];
	}
	if (!data.length) return;

	var dataMap = data.reduce(function (map, item) {
		if (!item || !item.id) return map;
		map[item.id] = item;
		return map;
	}, {});

	var imageEl = modal.querySelector('[data-bio-field="image"]');
	var mediaEl = modal.querySelector(".s-bio-modal__media");
	var bioModalEl = modal.querySelector(".s-bio-modal");
	var nameEl = modal.querySelector('[data-bio-field="name"]');
	var titleEl = modal.querySelector('[data-bio-field="title"]');
	var bioEl = modal.querySelector('[data-bio-field="bio"]');

	// HTML entities can survive into the JSON payload (e.g. typographic
	// apostrophes saved by TinyMCE as &#8217;). For text-only fields we
	// decode by round-tripping through a textarea — that turns named and
	// numeric entities back into real characters.
	var decoderEl = document.createElement("textarea");
	function decodeEntities(value) {
		if (!value) return "";
		decoderEl.innerHTML = String(value);
		return decoderEl.value;
	}

	function applyData(item) {
		if (!item) return;
		var hasImage = !!item.image;
		if (imageEl && hasImage) {
			imageEl.src = item.image;
			imageEl.alt = decodeEntities(item.name) || "Director portrait";
		}
		// Hide the media column entirely when this director has no portrait,
		// and let the content span full width via the --no-media modifier.
		if (mediaEl) mediaEl.hidden = !hasImage;
		if (bioModalEl) bioModalEl.classList.toggle("s-bio-modal--no-media", !hasImage);
		if (nameEl) nameEl.textContent = decodeEntities(item.name);
		if (titleEl) titleEl.textContent = decodeEntities(item.title);
		// Bio is a WYSIWYG payload (already sanitised server-side via
		// wp_kses_post), so render as HTML to preserve formatting.
		if (bioEl) bioEl.innerHTML = item.bio || "";
	}

	document.querySelectorAll("[data-bio-open]").forEach(function (trigger) {
		trigger.addEventListener("click", function (event) {
			event.preventDefault();
			var bioId = trigger.getAttribute("data-bio-open");
			applyData(dataMap[bioId] || data[0]);
			openModal("bio");
		});
	});
}

function initInViewDetect(delay) {
	var delayTime = delay || 0;
	var blocks = document.querySelectorAll(".in-view-detect, .in-view-detect-tag > *");
	if (!blocks.length) return;

	function handleEnter(el) {
		if (el.classList.contains("in-view")) return;
		el.classList.remove("in-view-detect");
		el.classList.add("in-view");
	}

	function threshold(el) {
		var offset = el.dataset.inViewOffset;
		if (offset) return parseFloat(offset);
		return Math.min(el.offsetHeight / 4, 120);
	}

	function fallbackInit() {
		function check() {
			var vh = window.innerHeight;
			blocks.forEach(function (item) {
				if (!item || item.classList.contains("in-view")) return;
				var rect = item.getBoundingClientRect();
				if (rect.top - vh <= -threshold(item)) {
					handleEnter(item);
				}
			});
		}
		window.addEventListener("scroll", check, { passive: true });
		window.addEventListener("resize", check);
		check();
	}

	function ioInit() {
		var observer = new IntersectionObserver(
			function (entries, obs) {
				var vh = window.innerHeight;
				entries.forEach(function (entry) {
					if (!entry.isIntersecting) return;
					var el = entry.target;
					if (el.classList.contains("in-view")) return;
					if (entry.boundingClientRect.top - vh <= -threshold(el)) {
						handleEnter(el);
						obs.unobserve(el);
					}
				});
			},
			{ root: null, threshold: [0, 0.01, 0.1, 0.25, 0.5] },
		);

		blocks.forEach(function (el) {
			observer.observe(el);
		});
	}

	setTimeout(function () {
		if ("IntersectionObserver" in window) {
			ioInit();
		} else {
			fallbackInit();
		}
	}, delayTime);
}

function initHeaderScroll() {
	var header = document.querySelector(".header");
	if (!header) return;
	var searchModal = document.querySelector(".s-modal--search");

	var wrapper = document.querySelector(".header-wrapper");
	var updateHeaderOffset = function () {
		if (!wrapper) return;
		var headerOffset = wrapper.getBoundingClientRect().top;
		document.body.style.setProperty(
			"--header-offset",
			(headerOffset > 0 ? headerOffset : 0) + "px",
		);
	};

	updateHeaderOffset();
	window.updateHeaderOffset = updateHeaderOffset;

	var ticking = false;
	var fixResetId = null;

	function setFixedState(isFixed) {
		header.classList.toggle("is-fixed", isFixed);
		if (searchModal) {
			searchModal.classList.toggle("is-fixed", isFixed);
		}
	}

	function addFixedInstant() {
		if (header.classList.contains("is-fixed")) return;
		header.style.transitionDuration = "0s";
		setFixedState(true);

		if (fixResetId) {
			clearTimeout(fixResetId);
		}
		fixResetId = setTimeout(function () {
			header.removeAttribute("style");
			fixResetId = null;
		}, 0);
	}

	function handleScroll() {
		if (document.body.classList.contains("modal-open")) return;
		if (Date.now() < suppressHeaderScrollUntil) {
			scrollTopPos = window.scrollY || window.pageYOffset || 0;
			return;
		}
		if (ticking) return;
		ticking = true;

		requestAnimationFrame(function () {
			var currentY = window.scrollY || window.pageYOffset;

			if (justClosedModal) {
				justClosedModal = false;
				if (currentY > 200) {
					addFixedInstant();
				} else {
					setFixedState(false);
				}
				if (currentY > 500) {
					header.classList.add("is-active");
				} else {
					header.classList.remove("is-active");
				}
				scrollTopPos = currentY;
				ticking = false;
				return;
			}

			if (header.classList.contains("is-sticky")) {
				if (currentY > 20) {
					header.classList.add("is-scrolling");
				} else {
					header.classList.remove("is-scrolling");
				}
				scrollTopPos = currentY;
				ticking = false;
				return;
			}

			var deltaY = currentY - scrollTopPos;
			var directionThreshold = 2;
			var scrollingUp = deltaY < -directionThreshold;
			var scrollingDown = deltaY > directionThreshold;

			if (currentY > 200) {
				addFixedInstant();
			} else {
				setFixedState(false);
			}

			if (currentY > 400 && scrollingUp) {
				header.classList.add("is-active");
			} else if (currentY <= 400 || scrollingDown) {
				header.classList.remove("is-active");
			}

			scrollTopPos = currentY;
			ticking = false;
		});
	}

	window.addEventListener("resize", updateHeaderOffset);
	window.addEventListener("scroll", handleScroll, { passive: true });
}

function initHeaderNavBg() {
	var header = document.querySelector(".header");
	if (!header) return;

	var nav = header.querySelector(".header__nav");
	if (!nav) return;

	var bg = nav.querySelector(".header__nav-bg");
	var list = nav.querySelector(":scope > .header__nav-list");
	if (!bg || !list) return;

	var items = Array.from(list.children);
	if (!items.length) return;

	var rafId = null;

	function showBgForLink(link) {
		if (!link) return;
		if (rafId) cancelAnimationFrame(rafId);
		rafId = requestAnimationFrame(function () {
			var linkRect = link.getBoundingClientRect();
			var navRect = nav.getBoundingClientRect();
			bg.style.width = Math.round(linkRect.width) + "px";
			bg.style.transform =
				"translateX(" + Math.round(linkRect.left - navRect.left) + "px)";
			bg.style.opacity = "1";
		});
	}

	function hideBg() {
		if (rafId) cancelAnimationFrame(rafId);
		bg.style.opacity = "0";
	}

	items.forEach(function (item) {
		var topLink = item.querySelector(":scope > .header__nav-link");
		if (!topLink) return;

		topLink.addEventListener("mouseenter", function () {
			showBgForLink(topLink);
		});

		var dropdown = item.querySelector(":scope > .header__dropdown");
		if (dropdown) {
			dropdown.addEventListener("mouseenter", function () {
				showBgForLink(topLink);
			});
			dropdown.addEventListener("mouseleave", hideBg);
		}

		item.addEventListener("mouseleave", hideBg);
	});

	nav.addEventListener("mouseleave", hideBg);

	window.addEventListener("resize", function () {
		var hovered = nav.querySelector(":scope > .header__nav-list > li:hover > .header__nav-link");
		if (hovered) {
			showBgForLink(hovered);
		} else {
			hideBg();
		}
	});
}

function initLenis() {
	if (typeof Lenis === "undefined") return;

	var lenis = new Lenis({
		smoothWheel: true,
		smoothTouch: false,
		lerp: 0.1,
		autoResize: false,
	});

	window.lenis = lenis;
	window.addEventListener("resize", function () {
		requestLenisResize();
	});
	requestLenisResize();

	function raf(time) {
		lenis.raf(time);
		requestAnimationFrame(raf);
	}
	requestAnimationFrame(raf);
}

function initHeroPlaneAnimation() {
	var hero = document.querySelector(".s-home-hero");
	if (!hero || typeof gsap === "undefined") return;
	var dashDrawLength = 12;
	var dashGapLength = 11;
	var dashStep = dashDrawLength + dashGapLength;
	var planeHalfSegment = 6;
	var planeMirrorX = 1;
	var planeMirrorY = 1;
	var planeStartLeadPx = 0;
	var planeEndLeadPx = 80;
	var mobileEndRotateFixDeg = -38;
	var isSmViewport =
		typeof window !== "undefined" &&
		window.matchMedia &&
		window.matchMedia("(max-width: 767.98px)").matches;
	planeEndLeadPx = isSmViewport ? 60 : 80;
	var scene = hero.querySelector(
		isSmViewport ? '[data-plane-scene="mobile"]' : '[data-plane-scene="desktop"]',
	);
	if (!scene) {
		scene = hero.querySelector("[data-plane-scene]");
	}
	if (!scene) return;

	var path = scene.querySelector("[data-plane-path]");
	var reveal = scene.querySelector("[data-plane-reveal]");
	var trail = scene.querySelector("[data-plane-trail]");
	var plane = scene.querySelector("[data-plane]");
	var planeSvg = plane ? plane.querySelector("[data-plane-svg]") : null;
	var planeGuideLine = plane ? plane.querySelector("[data-plane-guide-line]") : null;
	if (!path || !reveal || !trail || !plane || !planeSvg || !planeGuideLine) return;

	var totalLength = path.getTotalLength();
	var pathSegments = [];
	var lengthAt = 0;

	while (lengthAt <= totalLength) {
		var segmentStartLength = lengthAt;
		var segmentEndLength = Math.min(segmentStartLength + dashDrawLength, totalLength);
		if (segmentEndLength > segmentStartLength) {
			var segmentStartPoint = path.getPointAtLength(segmentStartLength);
			var segmentEndPoint = path.getPointAtLength(segmentEndLength);
			pathSegments.push({
				p1: { x: segmentStartPoint.x, y: segmentStartPoint.y },
				p2: { x: segmentEndPoint.x, y: segmentEndPoint.y },
			});
		}
		lengthAt += dashStep;
	}

	if (!pathSegments.length) return;

	function parseOrFallback(value, fallback) {
		var parsed = parseFloat(value);
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	function buildPlaneLocalSegment() {
		var viewBox = planeSvg.viewBox && planeSvg.viewBox.baseVal ? planeSvg.viewBox.baseVal : null;
		if (!viewBox || !viewBox.width || !viewBox.height) return null;

		var svgX = parseOrFallback(planeSvg.getAttribute("x"), 0);
		var svgY = parseOrFallback(planeSvg.getAttribute("y"), 0);
		var svgWidth = parseOrFallback(planeSvg.getAttribute("width"), viewBox.width);
		var svgHeight = parseOrFallback(planeSvg.getAttribute("height"), viewBox.height);
		var x1 = parseFloat(planeGuideLine.getAttribute("x1"));
		var y1 = parseFloat(planeGuideLine.getAttribute("y1"));
		var x2 = parseFloat(planeGuideLine.getAttribute("x2"));
		var y2 = parseFloat(planeGuideLine.getAttribute("y2"));

		if (
			!Number.isFinite(x1) ||
			!Number.isFinite(y1) ||
			!Number.isFinite(x2) ||
			!Number.isFinite(y2)
		) {
			return null;
		}

		var scaleX = svgWidth / viewBox.width;
		var scaleY = svgHeight / viewBox.height;

		function toPlaneLocal(x, y) {
			return {
				x: svgX + (x - viewBox.x) * scaleX,
				y: svgY + (y - viewBox.y) * scaleY,
			};
		}

		var lineStart = toPlaneLocal(x1, y1);
		var lineEnd = toPlaneLocal(x2, y2);
		var center = {
			x: (lineStart.x + lineEnd.x) / 2,
			y: (lineStart.y + lineEnd.y) / 2,
		};
		var lineDx = lineEnd.x - lineStart.x;
		var lineDy = lineEnd.y - lineStart.y;
		var lineLength = Math.hypot(lineDx, lineDy);
		if (!lineLength) return null;

		var unitX = lineDx / lineLength;
		var unitY = lineDy / lineLength;

		return {
			p1: { x: center.x + unitX * planeHalfSegment, y: center.y + unitY * planeHalfSegment },
			p2: { x: center.x - unitX * planeHalfSegment, y: center.y - unitY * planeHalfSegment },
		};
	}

	var planeLineSegment = buildPlaneLocalSegment();
	if (!planeLineSegment) return;

	function lerp(a, b, t) {
		return a + (b - a) * t;
	}

	function clamp(value, min, max) {
		return Math.max(min, Math.min(max, value));
	}

	function getSegmentAtProgress(progress) {
		if (pathSegments.length === 1) {
			return pathSegments[0];
		}

		var maxIndex = pathSegments.length - 1;
		if (progress < 0) {
			var first = pathSegments[0];
			var second = pathSegments[1];
			return {
				p1: {
					x: first.p1.x + (second.p1.x - first.p1.x) * progress,
					y: first.p1.y + (second.p1.y - first.p1.y) * progress,
				},
				p2: {
					x: first.p2.x + (second.p2.x - first.p2.x) * progress,
					y: first.p2.y + (second.p2.y - first.p2.y) * progress,
				},
			};
		}

		if (progress > maxIndex) {
			var prev = pathSegments[maxIndex - 1];
			var last = pathSegments[maxIndex];
			var extra = progress - maxIndex;
			return {
				p1: {
					x: last.p1.x + (last.p1.x - prev.p1.x) * extra,
					y: last.p1.y + (last.p1.y - prev.p1.y) * extra,
				},
				p2: {
					x: last.p2.x + (last.p2.x - prev.p2.x) * extra,
					y: last.p2.y + (last.p2.y - prev.p2.y) * extra,
				},
			};
		}

		var index = Math.floor(progress);
		var nextIndex = Math.min(index + 1, maxIndex);
		var ratio = progress - index;
		var current = pathSegments[index];
		var next = pathSegments[nextIndex];

		return {
			p1: {
				x: lerp(current.p1.x, next.p1.x, ratio),
				y: lerp(current.p1.y, next.p1.y, ratio),
			},
			p2: {
				x: lerp(current.p2.x, next.p2.x, ratio),
				y: lerp(current.p2.y, next.p2.y, ratio),
			},
		};
	}

	function setPlaneBySegmentProgress(progress) {
		var pathSegment = getSegmentAtProgress(progress);
		var localP1 = {
			x: planeLineSegment.p1.x * planeMirrorX,
			y: planeLineSegment.p1.y * planeMirrorY,
		};
		var localP2 = {
			x: planeLineSegment.p2.x * planeMirrorX,
			y: planeLineSegment.p2.y * planeMirrorY,
		};
		var pathP1 = pathSegment.p1;
		var pathP2 = pathSegment.p2;

		var localAngle = Math.atan2(localP2.y - localP1.y, localP2.x - localP1.x);
		var pathAngle = Math.atan2(pathP2.y - pathP1.y, pathP2.x - pathP1.x);
		var rotate = pathAngle - localAngle;

		if (isSmViewport) {
			var endFixStartProgress = Math.max(0, lastProgress * 0.88);
			var endFixMix = clamp(
				(progress - endFixStartProgress) / Math.max(0.0001, endProgress - endFixStartProgress),
				0,
				1,
			);
			rotate += (mobileEndRotateFixDeg * Math.PI / 180) * endFixMix;
		}

		var cos = Math.cos(rotate);
		var sin = Math.sin(rotate);

		var localCenterX = (localP1.x + localP2.x) / 2;
		var localCenterY = (localP1.y + localP2.y) / 2;
		var pathCenterX = (pathP1.x + pathP2.x) / 2;
		var pathCenterY = (pathP1.y + pathP2.y) / 2;
		var rotatedCenterX = localCenterX * cos - localCenterY * sin;
		var rotatedCenterY = localCenterX * sin + localCenterY * cos;
		var tx = pathCenterX - rotatedCenterX;
		var ty = pathCenterY - rotatedCenterY;

		plane.setAttribute(
			"transform",
			"matrix(" + cos + "," + sin + "," + -sin + "," + cos + "," + tx + "," + ty + ")",
		);
	}

	var lastProgress = pathSegments.length - 1;
	var startProgress = -(planeStartLeadPx / dashStep);
	var endProgress = lastProgress + planeEndLeadPx / dashStep;
	var basePathDuration = isSmViewport ? 2.4 : 4;
	var animationStartDelay =  .5;
	var flightEase = "sine.inOut";
	var safeLength = totalLength > 0 ? totalLength : 1;
	var startDelay = (planeStartLeadPx / safeLength) * basePathDuration;
	var endDelay = (planeEndLeadPx / safeLength) * basePathDuration;
	var syncDuration = basePathDuration + startDelay + endDelay;
	var pathDelayPx = planeEndLeadPx;
	var totalFlightLength = totalLength + planeStartLeadPx + planeEndLeadPx;
	var setDashOffset = null;
	var setPlaneOpacity = null;
	var setSceneOpacity = null;

	function renderAtProgress(progress) {
		if (!setDashOffset || !setPlaneOpacity || !setSceneOpacity) return;
		var progressRange = Math.max(0.0001, endProgress - startProgress);
		var ratio = clamp((progress - startProgress) / progressRange, 0, 1);
		var currentLength = ratio * totalFlightLength;
		var drawLength = clamp(currentLength - pathDelayPx, 0, totalLength);
		var fadeInLength = Math.min(totalLength * 0.08, 120);
		var flightOpacity = fadeInLength > 0 ? clamp(currentLength / fadeInLength, 0, 1) : 1;

		setDashOffset(totalLength - drawLength);
		setSceneOpacity(flightOpacity);
		setPlaneOpacity(currentLength > 0.5 ? 1 : 0);
	}

	if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		trail.removeAttribute("mask");
		gsap.set(scene, { opacity: 1 });
		gsap.set(plane, { opacity: 1 });
		setPlaneBySegmentProgress(endProgress);
		return;
	}

	gsap.set(reveal, {
		strokeDasharray: totalLength,
		strokeDashoffset: totalLength,
	});
	gsap.set(scene, { opacity: 0 });
	setDashOffset = gsap.quickSetter(reveal, "strokeDashoffset");
	setPlaneOpacity = gsap.quickSetter(plane, "opacity");
	setSceneOpacity = gsap.quickSetter(scene, "opacity");

	setPlaneBySegmentProgress(startProgress);
	renderAtProgress(startProgress);

	var segmentState = { progress: startProgress };

	var timeline = gsap.timeline({ paused: true });

	timeline.to(
		segmentState,
		{
			duration: syncDuration,
			ease: flightEase,
			progress: endProgress,
			onUpdate: function () {
				setPlaneBySegmentProgress(segmentState.progress);
				renderAtProgress(segmentState.progress);
			},
		},
		animationStartDelay,
	);

	var flightWrap = hero.querySelector(".s-home-hero__flight-wrap") || scene;
	var animationPlayed = false;
	var io = null;
	var mutation = null;

	function cleanupWatchers() {
		if (io) {
			io.disconnect();
			io = null;
		}
		if (mutation) {
			mutation.disconnect();
			mutation = null;
		}
	}

	function playOnce() {
		if (animationPlayed) return;
		animationPlayed = true;
		cleanupWatchers();
		timeline.play(0);
	}

	function isInViewport(el) {
		if (!el) return false;
		var rect = el.getBoundingClientRect();
		var vh = window.innerHeight || document.documentElement.clientHeight || 0;
		return rect.bottom > 0 && rect.top < vh;
	}

	if (flightWrap.classList.contains("in-view") || isInViewport(flightWrap)) {
		playOnce();
		return;
	}

	if ("IntersectionObserver" in window) {
		io = new IntersectionObserver(
			function (entries) {
				entries.forEach(function (entry) {
					if (!entry.isIntersecting) return;
					playOnce();
				});
			},
			{ threshold: 0.01 },
		);
		io.observe(flightWrap);
	}

	if (typeof MutationObserver !== "undefined") {
		mutation = new MutationObserver(function () {
			if (!flightWrap.classList.contains("in-view")) return;
			playOnce();
		});
		mutation.observe(flightWrap, { attributes: true, attributeFilter: ["class"] });
	}

	if (!io && !mutation) {
		var fallbackCheck = function () {
			if (!isInViewport(flightWrap)) return;
			window.removeEventListener("scroll", fallbackCheck);
			window.removeEventListener("resize", fallbackCheck);
			playOnce();
		};
		window.addEventListener("scroll", fallbackCheck, { passive: true });
		window.addEventListener("resize", fallbackCheck);
		fallbackCheck();
	}
}

function initPageHeroPlanePosition() {
	var heroes = Array.from(document.querySelectorAll(".s-page-hero"));
	if (!heroes.length) return;

	var scenes = heroes.reduce(function (allScenes, hero) {
		return allScenes.concat(Array.from(hero.querySelectorAll(".s-page-hero__flight")));
	}, []);
	if (!scenes.length) return;

	var planeHalfSegment = 6;

	function parseOrFallback(value, fallback) {
		var parsed = parseFloat(value);
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	function buildPlaneLocalSegment(planeSvg, planeGuideLine) {
		var viewBox = planeSvg.viewBox && planeSvg.viewBox.baseVal ? planeSvg.viewBox.baseVal : null;
		if (!viewBox || !viewBox.width || !viewBox.height) return null;

		var svgX = parseOrFallback(planeSvg.getAttribute("x"), 0);
		var svgY = parseOrFallback(planeSvg.getAttribute("y"), 0);
		var svgWidth = parseOrFallback(planeSvg.getAttribute("width"), viewBox.width);
		var svgHeight = parseOrFallback(planeSvg.getAttribute("height"), viewBox.height);
		var x1 = parseFloat(planeGuideLine.getAttribute("x1"));
		var y1 = parseFloat(planeGuideLine.getAttribute("y1"));
		var x2 = parseFloat(planeGuideLine.getAttribute("x2"));
		var y2 = parseFloat(planeGuideLine.getAttribute("y2"));

		if (
			!Number.isFinite(x1) ||
			!Number.isFinite(y1) ||
			!Number.isFinite(x2) ||
			!Number.isFinite(y2)
		) {
			return null;
		}

		var scaleX = svgWidth / viewBox.width;
		var scaleY = svgHeight / viewBox.height;

		function toPlaneLocal(x, y) {
			return {
				x: svgX + (x - viewBox.x) * scaleX,
				y: svgY + (y - viewBox.y) * scaleY,
			};
		}

		var lineStart = toPlaneLocal(x1, y1);
		var lineEnd = toPlaneLocal(x2, y2);
		var center = {
			x: (lineStart.x + lineEnd.x) / 2,
			y: (lineStart.y + lineEnd.y) / 2,
		};
		var lineDx = lineEnd.x - lineStart.x;
		var lineDy = lineEnd.y - lineStart.y;
		var lineLength = Math.hypot(lineDx, lineDy);
		if (!lineLength) return null;

		var unitX = lineDx / lineLength;
		var unitY = lineDy / lineLength;

		return {
			p1: { x: center.x + unitX * planeHalfSegment, y: center.y + unitY * planeHalfSegment },
			p2: { x: center.x - unitX * planeHalfSegment, y: center.y - unitY * planeHalfSegment },
		};
	}

	function setPlaneAtPathEnd(scene) {
		var path = scene.querySelector("defs path");
		var plane = scene.querySelector(".s-page-hero__flight-plane");
		var planeSvg = plane ? plane.querySelector("[data-plane-svg]") : null;
		var planeGuideLine = plane ? plane.querySelector("[data-plane-guide-line]") : null;
		if (!path || !plane || !planeSvg || !planeGuideLine) return;

		var totalLength = path.getTotalLength();
		if (!totalLength) return;

		var planeLineSegment = buildPlaneLocalSegment(planeSvg, planeGuideLine);
		if (!planeLineSegment) return;

		var sampleBack = Math.min(12, totalLength);
		var fromLength = Math.max(0, totalLength - sampleBack);
		var pathFrom = path.getPointAtLength(fromLength);
		var pathTo = path.getPointAtLength(totalLength);

		var localP1 = planeLineSegment.p1;
		var localP2 = planeLineSegment.p2;
		var localAngle = Math.atan2(localP2.y - localP1.y, localP2.x - localP1.x);
		var pathAngle = Math.atan2(pathTo.y - pathFrom.y, pathTo.x - pathFrom.x);
		var rotate = pathAngle - localAngle;
		var cos = Math.cos(rotate);
		var sin = Math.sin(rotate);

		var localCenterX = (localP1.x + localP2.x) / 2;
		var localCenterY = (localP1.y + localP2.y) / 2;
		var rotatedCenterX = localCenterX * cos - localCenterY * sin;
		var rotatedCenterY = localCenterX * sin + localCenterY * cos;
		var tx = pathTo.x - rotatedCenterX;
		var ty = pathTo.y - rotatedCenterY;

		plane.setAttribute(
			"transform",
			"matrix(" + cos + "," + sin + "," + -sin + "," + cos + "," + tx + "," + ty + ")",
		);
	}

	function render() {
		scenes.forEach(function (scene) {
			setPlaneAtPathEnd(scene);
		});
	}

	render();

	var resizeRaf = null;
	window.addEventListener("resize", function () {
		if (resizeRaf) cancelAnimationFrame(resizeRaf);
		resizeRaf = requestAnimationFrame(function () {
			render();
			resizeRaf = null;
		});
	});
}

function initDiscoverSlider() {
	var slider = document.querySelector("[data-discover-slider]");
	if (!slider || typeof Swiper === "undefined") return;

	var track = slider.querySelector("[data-discover-track]");
	var prevButton = slider.querySelector("[data-discover-prev]");
	var nextButton = slider.querySelector("[data-discover-next]");
	if (!track) return;

	function updateButtons(swiper) {
		if (!prevButton || !nextButton || !swiper) return;
		prevButton.disabled = !!swiper.isBeginning;
		nextButton.disabled = !!swiper.isEnd;
	}

	function updatePartialCards(swiper) {
		var cards = Array.from((swiper && swiper.slides) || []);
		if (!cards.length) return;

		var viewportRect = slider.getBoundingClientRect();
		var tolerance = 1;

		cards.forEach(function (card) {
			var cardRect = card.getBoundingClientRect();
			var isFullyVisible =
				cardRect.left >= viewportRect.left - tolerance &&
				cardRect.right <= viewportRect.right + tolerance;
			card.classList.toggle("is-partial", !isFullyVisible);
		});
	}

	new Swiper(slider, {
		modules: [Navigation],
		slidesPerView: "auto",
		spaceBetween: 52,
		speed: 300,
		resizeObserver: false,
		watchOverflow: true,
		navigation: {
			enabled: !!(prevButton && nextButton),
			prevEl: prevButton || null,
			nextEl: nextButton || null,
		},
		breakpoints: {
			0: {
				spaceBetween: 16,
				slidesOffsetBefore: 16,
				slidesOffsetAfter: 16,
			},
			768: {
				spaceBetween: 32,
				slidesOffsetBefore: 32,
				slidesOffsetAfter: 32,
			},
			992: {
				spaceBetween: 32,
				slidesOffsetBefore: 56,
				slidesOffsetAfter: 56,
			},
			1200: {
				spaceBetween: 52,
				slidesOffsetBefore: 84,
				slidesOffsetAfter: 84,
			},
		},
		on: {
			init: function () {
				updateButtons(this);
				updatePartialCards(this);
			},
			setTranslate: function () {
				updateButtons(this);
				updatePartialCards(this);
			},
			transitionEnd: function () {
				updateButtons(this);
				updatePartialCards(this);
			},
			resize: function () {
				updateButtons(this);
				updatePartialCards(this);
			},
		},
	});
}

function initMasterSlider() {
	if (typeof Swiper === "undefined") return;

	var sections = Array.from(document.querySelectorAll("[data-master-slider-section]"));
	if (!sections.length) return;

	sections.forEach(function (section) {
		var slider = section.querySelector("[data-master-slider]");
		var track = section.querySelector("[data-master-slider-track]");
		var prevButton = section.querySelector("[data-master-slider-prev]");
		var nextButton = section.querySelector("[data-master-slider-next]");
		if (!slider || !track) return;

		var slides = Array.from(track.children).filter(function (child) {
			return child.classList && child.classList.contains("swiper-slide");
		});
		var slidesCount = slides.length;
		if (!slidesCount) return;

		section.classList.remove("is-count-lt4", "is-count-eq4", "is-count-gt4", "has-nav");
		if (slidesCount < 4) {
			section.classList.add("is-count-lt4");
		} else if (slidesCount === 4) {
			section.classList.add("is-count-eq4");
		} else {
			section.classList.add("is-count-gt4");
		}

		var useNavigation = slidesCount > 4 && prevButton && nextButton;

		function updateNavigationVisibility() {
			var shouldShowNavigation = !!useNavigation;
			section.classList.toggle("has-nav", shouldShowNavigation);
			return shouldShowNavigation;
		}

		function updateButtons(swiper) {
			if (!updateNavigationVisibility()) {
				if (prevButton) prevButton.disabled = true;
				if (nextButton) nextButton.disabled = true;
				return;
			}
			prevButton.disabled = !!swiper.isBeginning;
			nextButton.disabled = !!swiper.isEnd;
		}

		if (!useNavigation) {
			section.classList.remove("has-nav");
			if (prevButton) prevButton.disabled = true;
			if (nextButton) nextButton.disabled = true;
		}

		new Swiper(slider, {
			modules: [Navigation],
			speed: 300,
			resizeObserver: false,
			watchOverflow: true,
			slidesPerView: "auto",
			spaceBetween: 16,
			navigation: {
				enabled: useNavigation,
				prevEl: prevButton || null,
				nextEl: nextButton || null,
			},
			breakpoints: {
				0: {
					slidesPerView: "auto",
					spaceBetween: 16,
				},
				768: {
					slidesPerView: 3,
					spaceBetween: 24,
				},
				1024: {
					slidesPerView: 4,
					spaceBetween: 24,
				},
			},
			on: {
				init: function () {
					updateButtons(this);
				},
				setTranslate: function () {
					updateButtons(this);
				},
				transitionEnd: function () {
					updateButtons(this);
				},
				resize: function () {
					updateButtons(this);
				},
			},
		});
	});
}

function initNewsCardSlider() {
	if (typeof Swiper === "undefined") return;

	var sections = Array.from(document.querySelectorAll("[data-news-card-slider-section]"));
	if (!sections.length) return;

	sections.forEach(function (section) {
		var slider = section.querySelector("[data-news-card-slider]");
		var prevButton = section.querySelector("[data-news-card-slider-prev]");
		var nextButton = section.querySelector("[data-news-card-slider-next]");
		if (!slider) return;

		// Only enable seamless looping when we have enough unique slides (3+).
		// Below 3, Swiper duplicates slides to fill the loop which causes visual repeats.
		var slideCount = slider.querySelectorAll(".swiper-slide").length;
		var shouldLoop = slideCount >= 3;

		function updateButtons(swiper) {
			if (!prevButton || !nextButton || !swiper) return;
			var isLocked = !!swiper.isLocked;
			prevButton.disabled = isLocked || !!swiper.isBeginning;
			nextButton.disabled = isLocked || !!swiper.isEnd;
		}

		new Swiper(slider, {
			modules: [Navigation],
			loop: shouldLoop,
			speed: 300,
			resizeObserver: false,
			watchOverflow: true,
			slidesPerView: 1.08,
			spaceBetween: 16,
			navigation: {
				enabled: !!(prevButton && nextButton),
				prevEl: prevButton || null,
				nextEl: nextButton || null,
			},
			breakpoints: {
				0: {
					slidesPerView: 1.08,
					spaceBetween: 16,
				},
				768: {
					slidesPerView: 2,
					spaceBetween: 20,
				},
				1200: {
					slidesPerView: 3,
					spaceBetween: 32,
				},
			},
			on: {
				init: function () {
					updateButtons(this);
				},
				setTranslate: function () {
					updateButtons(this);
				},
				transitionEnd: function () {
					updateButtons(this);
				},
				resize: function () {
					updateButtons(this);
				},
			},
		});
	});
}

function initDiscoverPlaneAnimation() {
	var section = document.querySelector(".s-discover");
	if (!section || typeof gsap === "undefined") return;

	var scene = section.querySelector("[data-discover-plane-scene]");
	var path = section.querySelector("[data-discover-plane-path]");
	var reveal = section.querySelector("[data-discover-plane-reveal]");
	var trail = section.querySelector("[data-discover-plane-trail]");
	var plane = section.querySelector("[data-discover-plane]");
	var planeSvg = plane ? plane.querySelector("[data-plane-svg]") : null;
	var planeGuideLine = plane ? plane.querySelector("[data-plane-guide-line]") : null;

	if (!scene || !path || !reveal || !trail || !plane || !planeSvg || !planeGuideLine) return;

	var totalLength = path.getTotalLength();
	if (!totalLength) return;
	var planeLeadOutPx = 80;

	function parseOrFallback(value, fallback) {
		var parsed = parseFloat(value);
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	function clamp(value, min, max) {
		return Math.max(min, Math.min(max, value));
	}

	function getEndDirectionUnit() {
		var sampleBack = Math.min(12, totalLength);
		var fromLength = Math.max(0, totalLength - sampleBack);
		var pFrom = path.getPointAtLength(fromLength);
		var pTo = path.getPointAtLength(totalLength);
		var dx = pTo.x - pFrom.x;
		var dy = pTo.y - pFrom.y;
		var len = Math.hypot(dx, dy);
		if (!len) return { x: 1, y: 0 };
		return { x: dx / len, y: dy / len };
	}

	var endDirectionUnit = getEndDirectionUnit();

	function buildPlaneLocalSegment() {
		var viewBox = planeSvg.viewBox && planeSvg.viewBox.baseVal ? planeSvg.viewBox.baseVal : null;
		if (!viewBox || !viewBox.width || !viewBox.height) return null;

		var svgX = parseOrFallback(planeSvg.getAttribute("x"), 0);
		var svgY = parseOrFallback(planeSvg.getAttribute("y"), 0);
		var svgWidth = parseOrFallback(planeSvg.getAttribute("width"), viewBox.width);
		var svgHeight = parseOrFallback(planeSvg.getAttribute("height"), viewBox.height);
		var x1 = parseFloat(planeGuideLine.getAttribute("x1"));
		var y1 = parseFloat(planeGuideLine.getAttribute("y1"));
		var x2 = parseFloat(planeGuideLine.getAttribute("x2"));
		var y2 = parseFloat(planeGuideLine.getAttribute("y2"));

		if (
			!Number.isFinite(x1) ||
			!Number.isFinite(y1) ||
			!Number.isFinite(x2) ||
			!Number.isFinite(y2)
		) {
			return null;
		}

		var scaleX = svgWidth / viewBox.width;
		var scaleY = svgHeight / viewBox.height;

		function toPlaneLocal(x, y) {
			return {
				x: svgX + (x - viewBox.x) * scaleX,
				y: svgY + (y - viewBox.y) * scaleY,
			};
		}

		var lineStart = toPlaneLocal(x1, y1);
		var lineEnd = toPlaneLocal(x2, y2);
		var center = {
			x: (lineStart.x + lineEnd.x) / 2,
			y: (lineStart.y + lineEnd.y) / 2,
		};
		var lineDx = lineEnd.x - lineStart.x;
		var lineDy = lineEnd.y - lineStart.y;
		var lineLength = Math.hypot(lineDx, lineDy);
		if (!lineLength) return null;

		var unitX = lineDx / lineLength;
		var unitY = lineDy / lineLength;
		var halfSegment = 6;

		return {
			p1: { x: center.x + unitX * halfSegment, y: center.y + unitY * halfSegment },
			p2: { x: center.x - unitX * halfSegment, y: center.y - unitY * halfSegment },
		};
	}

	var planeLineSegment = buildPlaneLocalSegment();
	if (!planeLineSegment) return;

	function getPathSegmentAtLength(length) {
		var headLength = 6;
		var tailLength = 6;
		var clampedLength = clamp(length, 0, totalLength);
		var p1Length = clamp(clampedLength - tailLength, 0, totalLength);
		var p2Length = clamp(clampedLength + headLength, 0, totalLength);

		if (p1Length === p2Length) {
			p2Length = clamp(p2Length + 0.001, 0, totalLength);
		}

		var p1 = path.getPointAtLength(p1Length);
		var p2 = path.getPointAtLength(p2Length);
		if (length > totalLength) {
			var leadOut = length - totalLength;
			p1 = {
				x: p1.x + endDirectionUnit.x * leadOut,
				y: p1.y + endDirectionUnit.y * leadOut,
			};
			p2 = {
				x: p2.x + endDirectionUnit.x * leadOut,
				y: p2.y + endDirectionUnit.y * leadOut,
			};
		}

		return {
			p1: p1,
			p2: p2,
		};
	}

	function setPlaneByLength(length) {
		var pathSegment = getPathSegmentAtLength(length);
		var localP1 = planeLineSegment.p1;
		var localP2 = planeLineSegment.p2;
		var pathP1 = pathSegment.p1;
		var pathP2 = pathSegment.p2;

		var localAngle = Math.atan2(localP2.y - localP1.y, localP2.x - localP1.x);
		var pathAngle = Math.atan2(pathP2.y - pathP1.y, pathP2.x - pathP1.x);
		var rotate = pathAngle - localAngle;
		var cos = Math.cos(rotate);
		var sin = Math.sin(rotate);

		var localCenterX = (localP1.x + localP2.x) / 2;
		var localCenterY = (localP1.y + localP2.y) / 2;
		var pathCenterX = (pathP1.x + pathP2.x) / 2;
		var pathCenterY = (pathP1.y + pathP2.y) / 2;
		var rotatedCenterX = localCenterX * cos - localCenterY * sin;
		var rotatedCenterY = localCenterX * sin + localCenterY * cos;
		var tx = pathCenterX - rotatedCenterX;
		var ty = pathCenterY - rotatedCenterY;

		plane.setAttribute(
			"transform",
			"matrix(" + cos + "," + sin + "," + -sin + "," + cos + "," + tx + "," + ty + ")",
		);
	}

	trail.removeAttribute("mask");
	gsap.set(reveal, {
		strokeDasharray: totalLength,
		strokeDashoffset: 0,
	});
	gsap.set(scene, { opacity: 1 });
	gsap.set(plane, { opacity: 1 });
	setPlaneByLength(totalLength + planeLeadOutPx);
}

function initFloatingImages() {
	if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;

	var items = Array.from(document.querySelectorAll("[data-float-scroll]"));
	if (!items.length) return;

	items.forEach(function (item) {
		var attr = parseFloat(item.getAttribute("data-float-range"));
		var range = Number.isFinite(attr) ? attr : 16;
		var intensity = 2;
		var yFrom = Math.round(range * 0.5 * intensity);
		var yTo = Math.round(-range * 0.5 * intensity);
		var trigger = item.closest("section") || item.parentElement || item;

		gsap.fromTo(
			item,
			{ y: yFrom },
			{
				y: yTo,
				ease: "none",
				scrollTrigger: {
					trigger: trigger,
					start: "top bottom",
					end: "bottom top",
					scrub: true,
				},
			},
		);
	});
}

function initResourcesBlobHover() {
	if (typeof gsap === "undefined") return;

	var blobs = Array.from(document.querySelectorAll(".s-resources__blob"));
	if (!blobs.length) return;

	var canHover =
		!window.matchMedia ||
		window.matchMedia("(hover: hover) and (pointer: fine)").matches;
	var canAnimateHover = canHover;

	var prefersReducedMotion =
		window.matchMedia &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	var hoverSamples = [
		"M64.6,-19.6C71.8,1,57.3,29.9,33.6,47.5C9.9,65,-23.1,71,-43.1,57C-63.1,43,-70.1,9,-60.8,-14.6C-51.4,-38.3,-25.7,-51.5,1.5,-52C28.7,-52.4,57.5,-40.2,64.6,-19.6Z",
		"M56.9,-21C63,0.5,50,25.7,30.2,39.7C10.3,53.7,-16.4,56.5,-30.9,45.2C-45.5,34,-48,8.8,-40.8,-14.1C-33.6,-37,-16.8,-57.6,4.3,-59C25.3,-60.4,50.7,-42.5,56.9,-21Z",
		"M62.6,-26.4C67.8,-4.300000000000001,49.5,19.3,28.8,32.7C8.1,46.1,-15,49.3,-28.2,39.5C-41.3,29.8,-44.5,7.1,-38.2,-16.5C-31.8,-40.2,-15.9,-64.9,6.4,-66.9C28.7,-69,57.4,-48.5,62.6,-26.4Z",
		"M58.4,-21.2C64.6,0.10000000000000009,50.9,25.7,28.2,43.2C5.5,60.7,-26.199999999999996,70.2,-45.7,57.3C-65.3,44.5,-72.6,9.2,-63,-16.8C-53.3,-42.8,-26.7,-59.6,-0.2999999999999998,-59.5C26.1,-59.4,52.2,-42.4,58.4,-21.2Z",
	];

	var scratchSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
	scratchSvg.setAttribute("width", "0");
	scratchSvg.setAttribute("height", "0");
	scratchSvg.style.position = "absolute";
	scratchSvg.style.left = "-9999px";
	scratchSvg.style.top = "-9999px";
	scratchSvg.style.opacity = "0";
	scratchSvg.style.pointerEvents = "none";
	document.body.appendChild(scratchSvg);

	function destroyScratch() {
		if (!scratchSvg || !scratchSvg.parentNode) return;
		scratchSvg.parentNode.removeChild(scratchSvg);
	}

	function samplePointsFromPathD(d, count) {
		var pathEl = document.createElementNS("http://www.w3.org/2000/svg", "path");
		pathEl.setAttribute("d", d);
		scratchSvg.appendChild(pathEl);

		var length = pathEl.getTotalLength();
		var points = [];

		for (var i = 0; i < count; i += 1) {
			var pointLength = (length * i) / count;
			var pt = pathEl.getPointAtLength(pointLength);
			points.push({ x: pt.x, y: pt.y });
		}

		scratchSvg.removeChild(pathEl);
		return points;
	}

	function getBox(points) {
		var minX = Infinity;
		var minY = Infinity;
		var maxX = -Infinity;
		var maxY = -Infinity;

		points.forEach(function (pt) {
			if (pt.x < minX) minX = pt.x;
			if (pt.y < minY) minY = pt.y;
			if (pt.x > maxX) maxX = pt.x;
			if (pt.y > maxY) maxY = pt.y;
		});

		return {
			minX: minX,
			minY: minY,
			maxX: maxX,
			maxY: maxY,
			width: Math.max(1, maxX - minX),
			height: Math.max(1, maxY - minY),
		};
	}

	function parseSvgViewBox(svg) {
		if (!svg) return null;

		var rawViewBox = svg.getAttribute("viewBox");
		if (!rawViewBox) return null;

		var parts = rawViewBox
			.trim()
			.split(/[\s,]+/)
			.map(function (part) {
				return parseFloat(part);
			});

		if (parts.length !== 4 || parts.some(function (value) { return !Number.isFinite(value); })) {
			return null;
		}

		var minX = parts[0];
		var minY = parts[1];
		var width = Math.max(1, parts[2]);
		var height = Math.max(1, parts[3]);

		return {
			minX: minX,
			minY: minY,
			maxX: minX + width,
			maxY: minY + height,
			width: width,
			height: height,
		};
	}

	function fitPointsToBox(points, targetBox) {
		var sourceBox = getBox(points);

		return points.map(function (pt) {
			return {
				x: ((pt.x - sourceBox.minX) / sourceBox.width) * targetBox.width + targetBox.minX,
				y: ((pt.y - sourceBox.minY) / sourceBox.height) * targetBox.height + targetBox.minY,
			};
		});
	}

	function clampValue(value, min, max) {
		return Math.max(min, Math.min(max, value));
	}

	function distanceSquared(a, b) {
		var dx = a.x - b.x;
		var dy = a.y - b.y;
		return dx * dx + dy * dy;
	}

	function rotatePoints(points, shift) {
		var len = points.length;
		var out = new Array(len);
		for (var i = 0; i < len; i += 1) {
			out[i] = points[(i + shift) % len];
		}
		return out;
	}

	function reversePoints(points) {
		return points.slice().reverse();
	}

	function getAlignmentScore(basePoints, candidatePoints) {
		var len = Math.min(basePoints.length, candidatePoints.length);
		if (!len) return Infinity;

		var score = 0;
		var step = Math.max(1, Math.floor(len / 12));
		for (var i = 0; i < len; i += step) {
			score += distanceSquared(basePoints[i], candidatePoints[i]);
		}

		score += distanceSquared(basePoints[0], candidatePoints[0]) * 6;
		if (len > 1) {
			score += distanceSquared(basePoints[1], candidatePoints[1]) * 3;
		}
		if (len > 2) {
			score += distanceSquared(basePoints[len - 1], candidatePoints[len - 1]) * 3;
		}

		return score;
	}

	function alignPointsToBase(basePoints, targetPoints) {
		var len = Math.min(basePoints.length, targetPoints.length);
		if (!len) return [];

		var source = targetPoints.slice(0, len);
		var orientations = [source, reversePoints(source)];
		var best = source;
		var bestScore = Infinity;

		orientations.forEach(function (oriented) {
			for (var shift = 0; shift < len; shift += 1) {
				var candidate = rotatePoints(oriented, shift);
				var score = getAlignmentScore(basePoints, candidate);
				if (score < bestScore) {
					bestScore = score;
					best = candidate;
				}
			}
		});

		return best;
	}

	function limitPointsDisplacement(basePoints, targetPoints, maxDistance) {
		var len = Math.min(basePoints.length, targetPoints.length);
		var maxDistSq = maxDistance * maxDistance;
		var out = new Array(len);

		for (var i = 0; i < len; i += 1) {
			var base = basePoints[i];
			var target = targetPoints[i];
			var dx = target.x - base.x;
			var dy = target.y - base.y;
			var distSq = dx * dx + dy * dy;

			if (distSq > maxDistSq && distSq > 0) {
				var dist = Math.sqrt(distSq);
				var ratio = maxDistance / dist;
				out[i] = {
					x: base.x + dx * ratio,
					y: base.y + dy * ratio,
				};
			} else {
				out[i] = { x: target.x, y: target.y };
			}
		}

		return out;
	}

	function personalizeHoverPoints(points, box, index) {
		var centerX = box.minX + box.width * 0.5;
		var centerY = box.minY + box.height * 0.5;
		var halfW = box.width * 0.5;
		var halfH = box.height * 0.5;
		var presets = [
			{ sx: 1.03, sy: 0.96, kx: 0.11, ky: -0.06, wave: 0.075, edge: 0.05, phase: 0.3 },
			{ sx: 0.97, sy: 1.04, kx: -0.08, ky: 0.09, wave: 0.09, edge: 0.045, phase: 1.2 },
			{ sx: 1.06, sy: 0.94, kx: 0.06, ky: 0.05, wave: 0.07, edge: 0.06, phase: 2.4 },
			{ sx: 0.98, sy: 1.02, kx: -0.12, ky: -0.04, wave: 0.085, edge: 0.04, phase: 3.1 },
			{ sx: 1.04, sy: 0.97, kx: 0.13, ky: 0.02, wave: 0.08, edge: 0.055, phase: 4.2 },
			{ sx: 0.95, sy: 1.06, kx: -0.09, ky: 0.08, wave: 0.072, edge: 0.05, phase: 5.1 },
			{ sx: 1.02, sy: 0.95, kx: 0.07, ky: -0.1, wave: 0.095, edge: 0.05, phase: 6.0 },
			{ sx: 0.99, sy: 1.03, kx: -0.05, ky: 0.11, wave: 0.082, edge: 0.045, phase: 6.8 },
			{ sx: 1.05, sy: 0.98, kx: 0.1, ky: -0.03, wave: 0.078, edge: 0.058, phase: 7.4 },
			{ sx: 0.96, sy: 1.01, kx: -0.11, ky: 0.06, wave: 0.088, edge: 0.052, phase: 8.2 },
		];
		var p = presets[index % presets.length];
		var count = Math.max(1, points.length - 1);

		return points.map(function (pt, i) {
			var nx = (pt.x - centerX) / halfW;
			var ny = (pt.y - centerY) / halfH;
			var t = i / count;
			var w1 = Math.sin((t * Math.PI * 2) + p.phase);
			var w2 = Math.cos((t * Math.PI * 4) - p.phase * 0.6);
			var bulge = 1 + (w1 * p.wave) + (w2 * p.wave * 0.5);

			var x = centerX + (pt.x - centerX) * p.sx * bulge;
			var y = centerY + (pt.y - centerY) * p.sy * bulge;

			x += ny * p.kx * box.width * 0.2;
			y += nx * p.ky * box.height * 0.2;
			x += nx * Math.abs(nx) * p.edge * box.width * 0.12;
			y += ny * Math.abs(ny) * p.edge * box.height * 0.12;

			return {
				x: clampValue(x, box.minX - box.width * 0.16, box.maxX + box.width * 0.16),
				y: clampValue(y, box.minY - box.height * 0.16, box.maxY + box.height * 0.16),
			};
		});
	}

	function formatNum(value) {
		var out = value.toFixed(3).replace(/\.?0+$/, "");
		return out === "-0" ? "0" : out;
	}

	function pointsToPath(points) {
		var len = points.length;
		if (!len) return "";
		if (len === 1) {
			return "M" + formatNum(points[0].x) + "," + formatNum(points[0].y) + "Z";
		}
		if (len === 2) {
			return (
				"M" +
				formatNum(points[0].x) +
				"," +
				formatNum(points[0].y) +
				"L" +
				formatNum(points[1].x) +
				"," +
				formatNum(points[1].y) +
				"Z"
			);
		}

		var firstMidX = (points[len - 1].x + points[0].x) * 0.5;
		var firstMidY = (points[len - 1].y + points[0].y) * 0.5;
		var d = "M" + formatNum(firstMidX) + "," + formatNum(firstMidY);

		for (var i = 0; i < len; i += 1) {
			var current = points[i];
			var next = points[(i + 1) % len];
			var midX = (current.x + next.x) * 0.5;
			var midY = (current.y + next.y) * 0.5;
			d +=
				"Q" +
				formatNum(current.x) +
				"," +
				formatNum(current.y) +
				" " +
				formatNum(midX) +
				"," +
				formatNum(midY);
		}

		return d + "Z";
	}

	function mixPoints(pointsA, pointsB, t) {
		var result = [];
		var len = Math.min(pointsA.length, pointsB.length);

		for (var i = 0; i < len; i += 1) {
			result.push({
				x: pointsA[i].x + (pointsB[i].x - pointsA[i].x) * t,
				y: pointsA[i].y + (pointsB[i].y - pointsA[i].y) * t,
			});
		}

		return result;
	}

	function smoothClosedPoints(points, blend, iterations) {
		var len = points.length;
		if (len < 3) return points.slice();

		var amount = clampValue(blend, 0, 1);
		var out = points.slice();

		for (var pass = 0; pass < iterations; pass += 1) {
			var next = new Array(len);
			for (var i = 0; i < len; i += 1) {
				var prev = out[(i - 1 + len) % len];
				var current = out[i];
				var after = out[(i + 1) % len];
				var avgX = (prev.x + current.x + after.x) / 3;
				var avgY = (prev.y + current.y + after.y) / 3;

				next[i] = {
					x: current.x + (avgX - current.x) * amount,
					y: current.y + (avgY - current.y) * amount,
				};
			}
			out = next;
		}

		return out;
	}

	function stopBlobTweens(blob) {
		if (blob._blobEnterTween) {
			blob._blobEnterTween.kill();
			blob._blobEnterTween = null;
		}
	}

	function renderBlobPath(blob) {
		var path = blob._blobPath;
		if (!path) return;

		var state = blob._blobState;
		var points;

		points = mixPoints(blob._blobBasePoints, blob._blobHoverPointsA, state.hover);

		path.setAttribute("d", pointsToPath(points));
	}

	function syncBlobToSvgSize(blob) {
		if (
			!blob ||
			!blob._blobSvg ||
			!blob._blobViewBox ||
			!blob._blobBasePointsRaw ||
			!blob._blobHoverPointsRawA
		) {
			return;
		}

		var svg = blob._blobSvg;
		var targetBox = blob._blobViewBox;

		svg.setAttribute(
			"viewBox",
			formatNum(targetBox.minX) +
				" " +
				formatNum(targetBox.minY) +
				" " +
				formatNum(targetBox.width) +
				" " +
				formatNum(targetBox.height),
		);
		svg.setAttribute("preserveAspectRatio", "none");
		svg.setAttribute("width", "100%");
		svg.setAttribute("height", "100%");

		blob._blobBasePoints = blob._blobBasePointsRaw;
		blob._blobHoverPointsA = blob._blobHoverPointsRawA;

		renderBlobPath(blob);
	}

	function playBlobHover(blob) {
		stopBlobTweens(blob);

		if (prefersReducedMotion) {
			blob._blobState.hover = 1;
			renderBlobPath(blob);
			return;
		}

		blob._blobEnterTween = gsap.to(blob._blobState, {
			duration: 0.48,
			ease: "elastic.out(1, 0.44)",
			hover: 1,
			onUpdate: function () {
				renderBlobPath(blob);
			},
		});
	}

	function resetBlobHover(blob) {
		stopBlobTweens(blob);

		if (prefersReducedMotion) {
			blob._blobState.hover = 0;
			renderBlobPath(blob);
			return;
		}

		blob._blobEnterTween = gsap.to(blob._blobState, {
			duration: 0.42,
			ease: "power3.out",
			hover: 0,
			onUpdate: function () {
				renderBlobPath(blob);
			},
		});
	}

	blobs.forEach(function (blob, index) {
		var path = blob.querySelector("[data-blob-path]");
		var svg = blob.querySelector(".s-resources__blob-svg");
		if (!path || !svg) return;

		var baseD = path.getAttribute("d");
		if (!baseD) return;

		var sourceViewBox = parseSvgViewBox(svg);
		var sampleCount = 96;
		var basePoints = samplePointsFromPathD(baseD, sampleCount);
		var baseBox = getBox(basePoints);
		var targetBox = sourceViewBox || baseBox;

		var sampleD1 = hoverSamples[(index * 3 + 1) % hoverSamples.length];
		var samplePoints1 = samplePointsFromPathD(sampleD1, sampleCount);
		var fitted1 = fitPointsToBox(samplePoints1, baseBox);
		var personalized1 = personalizeHoverPoints(fitted1, baseBox, index);
		var aligned1 = alignPointsToBase(basePoints, personalized1);
		var isTightBlob = !!blob.closest(".b-blobs-grid, .s-page-template__blobs-grid");
		var targetPoints = aligned1;

		if (isTightBlob) {
			targetPoints = mixPoints(basePoints, targetPoints, 0.58);
		}

		var maxPointShift = Math.min(baseBox.width, baseBox.height) * (isTightBlob ? 0.09 : 0.22);
		var limited1 = limitPointsDisplacement(basePoints, targetPoints, maxPointShift);

		if (isTightBlob) {
			limited1 = smoothClosedPoints(limited1, 0.75, 3);
			limited1 = limitPointsDisplacement(basePoints, limited1, maxPointShift);
		}

		var normalizedBasePoints = fitPointsToBox(basePoints, targetBox);
		var normalizedHoverPoints = fitPointsToBox(limited1, targetBox);

		blob._blobPath = path;
		blob._blobSvg = svg;
		blob._blobViewBox = targetBox;
		blob._blobBasePointsRaw = normalizedBasePoints;
		blob._blobHoverPointsRawA = normalizedHoverPoints;
		blob._blobBasePoints = normalizedBasePoints;
		blob._blobHoverPointsA = normalizedHoverPoints;
		blob._blobState = { hover: 0 };

		syncBlobToSvgSize(blob);

		if (canAnimateHover) {
			blob.addEventListener("pointerenter", function () {
				playBlobHover(blob);
			});
			blob.addEventListener("pointerleave", function () {
				resetBlobHover(blob);
			});
			blob.addEventListener("pointercancel", function () {
				resetBlobHover(blob);
			});
		}
		blob.addEventListener("focus", function () {
			playBlobHover(blob);
		});
		blob.addEventListener("blur", function () {
			resetBlobHover(blob);
		});
	});

	var resizeScheduled = false;
	window.addEventListener("resize", function () {
		if (resizeScheduled) return;

		resizeScheduled = true;
		requestAnimationFrame(function () {
			blobs.forEach(function (blob) {
				syncBlobToSvgSize(blob);
			});
			resizeScheduled = false;
		});
	});

	destroyScratch();
}

function initStatsCountUp() {
	var values = Array.from(document.querySelectorAll("[data-countup]"));
	if (!values.length) return;

	var section = document.querySelector(".s-stats");
	var formatter = new Intl.NumberFormat("en-US");
	var prefersReducedMotion =
		window.matchMedia &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	function buildStartValue(endFormatted, endValue) {
		var metFirstDigit = false;
		var startFormatted = endFormatted
			.split("")
			.map(function (char) {
				if (char >= "0" && char <= "9") {
					if (!metFirstDigit) {
						metFirstDigit = true;
						return "1";
					}
					return "0";
				}
				return char;
			})
			.join("");

		var parsed = parseInt(startFormatted.replace(/[^0-9]/g, ""), 10);
		if (!Number.isFinite(parsed)) return 0;
		if (parsed >= endValue) return Math.max(0, Math.floor(endValue * 0.2));
		return parsed;
	}

	function setFixedNumberWidth(numberEl, finalText) {
		if (!numberEl || !finalText) return;

		var styles = window.getComputedStyle(numberEl);
		var measure = document.createElement("span");
		measure.textContent = finalText;
		measure.style.position = "absolute";
		measure.style.left = "-9999px";
		measure.style.top = "-9999px";
		measure.style.visibility = "hidden";
		measure.style.whiteSpace = "pre";
		measure.style.fontFamily = styles.fontFamily;
		measure.style.fontSize = styles.fontSize;
		measure.style.fontWeight = styles.fontWeight;
		measure.style.fontStyle = styles.fontStyle;
		measure.style.lineHeight = styles.lineHeight;
		measure.style.letterSpacing = styles.letterSpacing;
		measure.style.fontVariantNumeric = styles.fontVariantNumeric;
		measure.style.textTransform = styles.textTransform;

		document.body.appendChild(measure);
		var width = Math.ceil(measure.getBoundingClientRect().width);
		document.body.removeChild(measure);

		numberEl.style.width = width + "px";
	}

	function syncFixedWidths() {
		values.forEach(function (valueEl) {
			var numberEl = valueEl.querySelector("[data-countup-number]");
			if (!numberEl) return;
			var endValue = parseInt(valueEl.getAttribute("data-countup-end"), 10);
			if (!Number.isFinite(endValue)) return;
			setFixedNumberWidth(numberEl, formatter.format(endValue));
		});
	}

	function animateValue(valueEl, delay) {
		var numberEl = valueEl.querySelector("[data-countup-number]");
		if (!numberEl) return;

		var endValue = parseInt(valueEl.getAttribute("data-countup-end"), 10);
		if (!Number.isFinite(endValue)) return;

		var endFormatted = formatter.format(endValue);
		var startValue = buildStartValue(endFormatted, endValue);
		setFixedNumberWidth(numberEl, endFormatted);

		if (prefersReducedMotion || startValue === endValue) {
			numberEl.textContent = endFormatted;
			return;
		}

		numberEl.textContent = formatter.format(startValue);

		var duration = 1350;
		var startTime = null;

		function easeOutCubic(progress) {
			return 1 - Math.pow(1 - progress, 3);
		}

		function step(now) {
			if (startTime === null) startTime = now;
			var elapsed = now - startTime;
			var progress = Math.min(elapsed / duration, 1);
			var eased = easeOutCubic(progress);
			var current = Math.round(startValue + (endValue - startValue) * eased);
			numberEl.textContent = formatter.format(current);

			if (progress < 1) {
				window.requestAnimationFrame(step);
			} else {
				numberEl.textContent = endFormatted;
			}
		}

		window.setTimeout(function () {
			window.requestAnimationFrame(step);
		}, delay);
	}

	function run() {
		values.forEach(function (valueEl, index) {
			if (valueEl.dataset.countupPlayed === "true") return;
			valueEl.dataset.countupPlayed = "true";
			animateValue(valueEl, index * 90);
		});
	}

	if (!section || !("IntersectionObserver" in window)) {
		run();
		return;
	}

	var observer = new IntersectionObserver(
		function (entries, obs) {
			entries.forEach(function (entry) {
				if (!entry.isIntersecting) return;
				run();
				obs.disconnect();
			});
		},
		{
			threshold: 0.3,
			rootMargin: "0px 0px -10% 0px",
		},
	);

	observer.observe(section);

	syncFixedWidths();
	var resizeId = null;
	window.addEventListener("resize", function () {
		if (resizeId) cancelAnimationFrame(resizeId);
		resizeId = requestAnimationFrame(function () {
			syncFixedWidths();
			resizeId = null;
		});
	});

	if (document.fonts) {
		if (typeof document.fonts.ready === "object" && typeof document.fonts.ready.then === "function") {
			document.fonts.ready.then(function () {
				syncFixedWidths();
			});
		}

		if (typeof document.fonts.addEventListener === "function") {
			document.fonts.addEventListener("loadingdone", function () {
				syncFixedWidths();
			});
		}
	}
}

function initFooterGformSubmission() {
	var wrappers = document.querySelectorAll(".footer__gform");
	if (!wrappers.length) return;

	wrappers.forEach(function (wrapper) {
		if (wrapper._gformObserverInitialized) return;
		wrapper._gformObserverInitialized = true;

		var handleFormSubmission = function () {
			wrapper.classList.add("is-submitted");
		};

		var checkForConfirmation = function () {
			return !!(
				wrapper.querySelector(".gform_confirmation_message") ||
				wrapper.querySelector(".GF_AJAX_POSTBACK") ||
				wrapper.querySelector("[class*='gform_confirmation']")
			);
		};

		if (checkForConfirmation()) {
			handleFormSubmission();
			return;
		}

		var observer = new MutationObserver(function (mutations) {
			if (checkForConfirmation()) {
				handleFormSubmission();
				observer.disconnect();
			}
		});

		observer.observe(wrapper, {
			childList: true,
			subtree: true,
			attributes: false,
			characterData: false,
		});

		var forms = wrapper.querySelectorAll("form");
		forms.forEach(function (form) {
			if (form._gformListenerAttached) return;
			form._gformListenerAttached = true;

			form.addEventListener("submit", function () {
				setTimeout(function () {
					if (checkForConfirmation()) {
						handleFormSubmission();
						observer.disconnect();
					}
				}, 100);
			});
		});
	});
}

function initGravityFormResizeObservers() {
	var formWrappers = document.querySelectorAll(".gform_wrapper");
	if (!formWrappers.length) return;

	formWrappers.forEach(function (wrapper) {
		if (wrapper._lenisResizeObserverInitialized) return;
		wrapper._lenisResizeObserverInitialized = true;

		var observer = new MutationObserver(function (mutations) {
			requestLenisResize();
		});

		observer.observe(wrapper, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["class", "style"],
		});
	});
}

function initSmoothAnchors() {
	function getScrollOffset() {
		var header = document.querySelector(".header");
		var headerHeight = header ? header.offsetHeight : 0;
		return headerHeight + 24;
	}

	function scrollToTarget(target) {
		if (!target) return;
		var offset = getScrollOffset();
		var top = target.getBoundingClientRect().top + window.scrollY - offset;
		window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
	}

	document.addEventListener("click", function (event) {
		var link = event.target.closest('a[href^="#"]');
		if (!link) return;

		var href = link.getAttribute("href");
		// Skip bare "#" (non-functional placeholder links).
		if (!href || href === "#") return;

		// Only handle same-document anchors (no separate pathname prefix).
		var hash = href.indexOf("#") === 0 ? href.slice(1) : null;
		if (!hash) return;

		// Ignore anchors that target interactive widgets managed elsewhere
		// (e.g. modal toggles, accordion triggers, tabs — they use data- attrs instead).
		if (link.hasAttribute("data-modal-toggle")) return;
		if (link.hasAttribute("data-accordion-trigger")) return;

		var target;
		try {
			target = document.getElementById(hash) || document.querySelector('[name="' + hash + '"]');
		} catch (e) {
			return;
		}
		if (!target) return;

		event.preventDefault();
		scrollToTarget(target);

		// Update the URL hash without triggering another scroll.
		if (history.replaceState) {
			history.replaceState(null, "", "#" + hash);
		}
	});

	// Handle the initial page load when a hash is present in the URL.
	if (window.location.hash && window.location.hash.length > 1) {
		var initialHash = window.location.hash.slice(1);
		var initialTarget;
		try {
			initialTarget = document.getElementById(initialHash);
		} catch (e) {
			initialTarget = null;
		}
		if (initialTarget) {
			// Defer so layout is settled (web fonts, images, etc.).
			window.addEventListener("load", function () {
				setTimeout(function () {
					scrollToTarget(initialTarget);
				}, 50);
			});
		}
	}
}

document.addEventListener("DOMContentLoaded", function () {
	suppressResizeObserverLoopError();
	initInViewDetect(60);
	initSmoothAnchors();
	initModals();
	initAccordions();
	initEventsFilters();
	initNewsResourcesFilters();
	initDirectorBioModal();
	initHeaderScroll();
	initHeaderNavBg();
	initHeroPlaneAnimation();
	initPageHeroPlanePosition();
	initMasterSlider();
	initNewsCardSlider();
	initDiscoverSlider();
	initDiscoverPlaneAnimation();
	initFloatingImages();
	initResourcesBlobHover();
	initStatsCountUp();
	initLenis();
	initGravityFormResizeObservers();
	syncMenuButtons();
	initFooterGformSubmission();
});
